import {
  BOGGLE_DEFAULT_SEED,
  BOGGLE_INTERVAL_PREP_MS,
  BOGGLE_INVALID_FEEDBACK_MS,
  BOGGLE_MAX_VOWEL_RATIO_EXCLUSIVE,
  BOGGLE_MIN_VOWEL_RATIO,
  BOGGLE_REPEAT_TILE_MIN_GAP,
  BOGGLE_RESOLVE_ANIMATION_MS,
  BOGGLE_ROTATE_ANIMATION_MS,
  BOGGLE_ROTATE_GRAVITY_ANIMATION_MS,
} from '../../config/defaults'
import {
  BOGGLE_LETTER_WEIGHTS,
  flattenBoggleLetterWeights,
} from '../../config/boardDistributions/letterWeights'
import { getBoggleResolvedMaxWordLength } from '../../lib/challenge/resolvedModOps/outcomeQueries'
import {
  BOGGLE_OVERTIME_ACCELERATION_PER_SECOND,
  BOGGLE_OVERTIME_BACKSPACE_ESCALATION_EXPONENT,
  BOGGLE_OVERTIME_BACKSPACE_FREE_PRESSES,
  BOGGLE_OVERTIME_BACKSPACE_PENALTY_PER_TIER,
  BOGGLE_OVERTIME_BACKSPACE_VELOCITY_PER_TIER,
  BOGGLE_OVERTIME_LETTER_PRESS_PAUSE_MS,
  BOGGLE_OVERTIME_START_VALUE,
} from '../../content/timer'
import {
  DEFAULT_BOGGLE_PLAYGROUND_SETTINGS,
  type BoggleLetterAdjacencyMode,
  type BogglePlaygroundSettings,
  type BogglePlaygroundSettingsInput,
} from '../../config/playground'
import {
  areBogglePlaygroundSettingsEqual,
  getBoggleGridDimensions,
  getBoggleSkillUses,
  normalizeBogglePlaygroundSettings,
  toBoggleGridSize,
} from '../../lib/playground'
import {
  applyBoggleMeterDelta,
  countBoggleMeterUncommonLetters,
  createBoggleMeterState,
  decayBoggleMeter,
  getBoggleMeterDisplayedChargeRatio,
  getBoggleMeterTileSpawnIntervalMs,
  getBoggleMeterAcceptedWordIncrease,
  getMeterBurstDrainRampMs,
  getMeterBurstDrainTimeRemainingMs,
  getMeterBurstMaxDecayPerSecond,
} from '../../lib/meter'
import {
  findGoldRushBurst,
  isGoldRushBurstActive,
  isMeterBurstEffectActive,
} from '../../lib/meterBursts'
import {
  cloneBoggleLetterBags,
  collapseBoardDownward,
  createBoggleLetterBags,
  drawLetterFromBag,
  fillLetterBoardFromBags,
  gridTryBeginSelection,
  gridUpdateSelection,
  isAdjacentPosition,
  nextInteger,
  normalizeSeed,
  rotateLetterBoard,
  type BoggleLetterBags,
  type GridDimensions,
  type LetterWeight,
} from '../core'
import {
  BOGGLE_GOLD_TILE_KIND,
  BOGGLE_SPECIAL_TILE_KINDS,
  buildEffectiveSpecialTileCounts,
  buildPhysicalSpecialTileCounts,
  countBoggleSpecialTiles,
  countBoggleSpecialTilesInPath,
  countBoggleSpecialTilesOnBoard,
  createEmptyBoggleSpecialTileCadenceCounts,
  getBoggleSpecialTileTimeBonusMs,
  getEffectiveGoldCount,
  hasAnyBoggleSpecialTile,
  placeBoggleSpecialTilesOnBoard,
  placeBoggleSpecialTilesOnSpawnChance,
  placeBoggleSpecialTilesOnSpawnCadence,
  placeBoggleSpecialTilesOnSpawnedCells,
  placeBoggleSpecialTilesForResetWhenBoardEmpty,
  type BoggleSpecialTileCadenceCounts,
  type BoggleSpecialTileKind,
} from '../specialTiles'
import {
  countEffectiveBoggleGoldTilesInPath,
  isBoggleGoldRushActive,
} from './effectiveSpecialTiles'
import { standardLexicon } from './lexicon'
import { BOGGLE_VOWELS, partitionLetterWeights, pickVowelSpawnCount } from './refill'
import type {
  BoggleAnimatedCell,
  BoggleBoard,
  BoggleCell,
  BoggleClearedCell,
  BoggleGameState,
  BogglePosition,
  BoggleResetOptions,
  BoggleRotationDirection,
  BoggleRunLedgerAcceptedWordPathStep,
  BoggleRunLedgerPhase,
  BoggleRunEndReason,
  BoggleSelectionChange,
  BoggleSelectionMode,
  BoggleSkillMode,
  BoggleSkillSelection,
  CreateBoggleGameOptions,
} from './types'

/** Special tile kinds stripped when contagion overwrites neighboring letters. */
const BOGGLE_SPECIAL_KIND_SET = new Set<string>(BOGGLE_SPECIAL_TILE_KINDS)

function uniqueClearedPositionsAlongPath(path: readonly BogglePosition[]): BogglePosition[] {
  const seen = new Set<string>()
  const out: BogglePosition[] = []
  for (const position of path) {
    const key = `${position.col},${position.row}`
    if (!seen.has(key)) {
      seen.add(key)
      out.push({ col: position.col, row: position.row })
    }
  }
  return out
}

function appendRunEndLedger(
  state: BoggleGameState,
  atMs: number,
  endReason: BoggleRunEndReason,
): BoggleGameState['runLedger'] {
  const sumAcceptedWordLengths = state.scoredWords.reduce((sum, word) => sum + word.length, 0)
  return [
    ...state.runLedger,
    {
      kind: 'run-end' as const,
      index: state.runLedger.length,
      atMs,
      endReason,
      acceptedWordCount: state.scoredWords.length,
      acceptedWords: [...state.scoredWords],
      sumAcceptedWordLengths,
    },
  ]
}

function appendSemanticAction<
  TAction extends
    | {
        actionKind: 'selected-letter'
        letter: string
        position: BogglePosition
      }
    | {
        actionKind: 'submit'
        word: string
      },
>(state: BoggleGameState, action: TAction): BoggleGameState {
  return {
    ...state,
    runLedger: [
      ...state.runLedger,
      {
        ...action,
        atMs: state.timeMs,
        index: state.runLedger.length,
        kind: 'semantic-action',
      },
    ],
  }
}

function appendPhaseStartLedger(
  state: BoggleGameState,
  phase: BoggleRunLedgerPhase,
): BoggleGameState {
  return {
    ...state,
    runLedger: [
      ...state.runLedger,
      {
        atMs: state.timeMs,
        index: state.runLedger.length,
        kind: 'phase',
        phase,
        transition: 'start',
      },
    ],
  }
}

function forceContagionSpreadKindOnNeighbor(
  board: BoggleBoard,
  spawned: BoggleAnimatedCell[],
  position: BogglePosition,
  kind: BoggleSpecialTileKind,
): boolean {
  const cell = board[position.row]?.[position.col]
  if (!cell) {
    return false
  }

  const prevTags = cell.tags ?? []
  const withoutKinds = prevTags.filter(tag => !BOGGLE_SPECIAL_KIND_SET.has(tag))
  const nextTags = [...withoutKinds, kind]

  const unchanged =
    nextTags.length === prevTags.length && nextTags.every((tag, i) => prevTags[i] === tag)

  if (unchanged) {
    return false
  }

  board[position.row]![position.col] = {
    ...cell,
    tags: nextTags,
  }

  const spawnedIndex = spawned.findIndex(
    entry => entry.toCol === position.col && entry.toRow === position.row,
  )

  if (spawnedIndex !== -1) {
    const spawnedCell = spawned[spawnedIndex]!
    spawned[spawnedIndex] = {
      ...spawnedCell,
      tags: nextTags,
    }
  }

  return true
}

function getResolvedLetterWeights(
  resolvedChallenge: BoggleGameState['resolvedChallenge'],
) {
  return (
    resolvedChallenge?.gameplay.letterWeights ??
    flattenBoggleLetterWeights(BOGGLE_LETTER_WEIGHTS)
  )
}

function getResolvedRefillLetterWeights(
  resolvedChallenge: BoggleGameState['resolvedChallenge'],
) {
  return partitionLetterWeights(getResolvedLetterWeights(resolvedChallenge))
}

function countBoardLetters(board: BoggleBoard) {
  let vowels = 0
  let consonants = 0

  for (const row of board) {
    for (const cell of row) {
      if (!cell) {
        continue
      }

      if (BOGGLE_VOWELS.has(cell.letter)) {
        vowels += 1
      } else {
        consonants += 1
      }
    }
  }

  return { consonants, vowels }
}

function getResolvedVowelRatioBand(
  resolvedChallenge: BoggleGameState['resolvedChallenge'],
) {
  const band = resolvedChallenge?.gameplay.vowelRatioBand
  if (band) {
    return band
  }
  return {
    maxVowelRatioExclusive: BOGGLE_MAX_VOWEL_RATIO_EXCLUSIVE,
    minVowelRatio: BOGGLE_MIN_VOWEL_RATIO,
  }
}

function isValidOpeningBoard(
  board: BoggleBoard,
  resolvedChallenge: BoggleGameState['resolvedChallenge'],
) {
  const { maxVowelRatioExclusive, minVowelRatio } =
    getResolvedVowelRatioBand(resolvedChallenge)
  const { consonants, vowels } = countBoardLetters(board)
  const totalLetters = consonants + vowels
  const vowelRatio = totalLetters === 0 ? 0 : vowels / totalLetters

  return (
    consonants > vowels &&
    vowelRatio >= minVowelRatio &&
    vowelRatio < maxVowelRatioExclusive
  )
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value))
}

function getIntervalDurationMs(settings: BogglePlaygroundSettings) {
  return settings.intervalCount === null ? null : settings.intervalTimeSeconds * 1000
}

function getIntervalTimeRemainingMs(
  state: Pick<BoggleGameState, 'intervalDeadlineMs' | 'timeMs'>,
) {
  return state.intervalDeadlineMs === null
    ? null
    : Math.max(0, state.intervalDeadlineMs - state.timeMs)
}

function armIntervalAfterPrep(state: BoggleGameState): BoggleGameState {
  if (state.intervalPlayBeginsAtMs === null) {
    return state
  }

  if (state.timeMs < state.intervalPlayBeginsAtMs) {
    return state
  }

  if (state.intervalDeadlineMs !== null) {
    return {
      ...state,
      intervalPlayBeginsAtMs: null,
    }
  }

  if (state.intervalDurationMs === null) {
    return {
      ...state,
      intervalPlayBeginsAtMs: null,
    }
  }

  return {
    ...state,
    intervalDeadlineMs: state.timeMs + state.intervalDurationMs,
    intervalPlayBeginsAtMs: null,
  }
}

export function isIntervalPrepBlockingPlay(state: BoggleGameState) {
  return (
    state.intervalPlayBeginsAtMs !== null && state.timeMs < state.intervalPlayBeginsAtMs
  )
}

function countVowelsAlongPath(board: BoggleBoard, path: readonly BogglePosition[]) {
  let vowels = 0

  for (const pos of path) {
    const letter = board[pos.row]?.[pos.col]?.letter
    if (letter && BOGGLE_VOWELS.has(letter)) {
      vowels += 1
    }
  }

  return vowels
}

function getIntervalIndex(
  state: Pick<BoggleGameState, 'intervalsRemaining' | 'playgroundSettings'>,
) {
  const { intervalCount } = state.playgroundSettings
  if (intervalCount === null || state.intervalsRemaining === null) {
    return null
  }

  return intervalCount - state.intervalsRemaining + 1
}

function isPlaying(state: BoggleGameState) {
  return state.phase === 'playing'
}

function requiresStepAdjacency(state: BoggleGameState) {
  return state.letterAdjacencyMode === 'adjacent'
}

function forbidsStepAdjacency(state: BoggleGameState) {
  return state.letterAdjacencyMode === 'non-adjacent'
}

function getRepeatTileMinGap(letterAdjacencyMode: BoggleLetterAdjacencyMode) {
  return letterAdjacencyMode === 'adjacent' ? BOGGLE_REPEAT_TILE_MIN_GAP : 0
}

function getResolvedRepeatTileMinGap(
  letterAdjacencyMode: BoggleLetterAdjacencyMode,
  dimensions: GridDimensions,
  _resolvedChallenge: BoggleGameState['resolvedChallenge'],
) {
  void dimensions
  return getRepeatTileMinGap(letterAdjacencyMode)
}

function getStateDimensions(
  state: Pick<BoggleGameState, 'cols' | 'rows'>,
): GridDimensions {
  return {
    cols: state.cols,
    rows: state.rows,
  }
}

function hasSelectionPosition(
  selection: readonly BogglePosition[],
  position: BogglePosition,
) {
  return selection.some(entry => entry.row === position.row && entry.col === position.col)
}

function getMaxWordLength(resolvedChallenge: BoggleGameState['resolvedChallenge']) {
  return getBoggleResolvedMaxWordLength(resolvedChallenge)
}

function getWordLengthValidationMessage(
  state: Pick<BoggleGameState, 'minWordLength' | 'resolvedChallenge'>,
  wordLength: number,
) {
  const maxWordLength = getMaxWordLength(state.resolvedChallenge)
  if (maxWordLength !== null && wordLength > maxWordLength) {
    return `Word must have ${maxWordLength} letters or fewer`
  }

  if (wordLength < state.minWordLength) {
    return `Word must have ${state.minWordLength} or more letters`
  }

  return null
}

function getSelectionAdjacencyValidationMessage(
  selection: readonly BogglePosition[],
  {
    allowDiagonals,
    letterAdjacencyMode,
  }: Pick<BoggleGameState, 'allowDiagonals' | 'letterAdjacencyMode'>,
) {
  for (let index = 1; index < selection.length; index += 1) {
    const previous = selection[index - 1]
    const current = selection[index]
    if (!previous || !current) {
      continue
    }

    const isAdjacentStep = isAdjacentPosition(previous, current, allowDiagonals)
    const doubledSameTile = previous.row === current.row && previous.col === current.col
    if (letterAdjacencyMode === 'adjacent' && !isAdjacentStep && !doubledSameTile) {
      return 'Letters must connect to adjacent tiles.'
    }

    if (
      letterAdjacencyMode === 'non-adjacent' &&
      isAdjacentPosition(previous, current, false)
    ) {
      return 'Consecutive tiles may not share a side.'
    }
  }

  return null
}

function getWordRuleValidationMessage(
  state: Pick<
    BoggleGameState,
    | 'allowDiagonals'
    | 'letterAdjacencyMode'
    | 'minWordLength'
    | 'resolvedChallenge'
    | 'selection'
  >,
  word: string,
) {
  const adjacencyValidationMessage = getSelectionAdjacencyValidationMessage(
    state.selection,
    state,
  )
  if (adjacencyValidationMessage !== null) {
    return adjacencyValidationMessage
  }

  const wordLengthValidationMessage = getWordLengthValidationMessage(state, word.length)
  if (wordLengthValidationMessage !== null) {
    return wordLengthValidationMessage
  }

  return null
}

function isSubmittedWordInDictionary(state: BoggleGameState, word: string) {
  return state.devAcceptAnyDictionaryWord || standardLexicon.isDictionaryWord(word)
}

function isAlreadyScoredWord(
  state: Pick<BoggleGameState, 'playgroundSettings' | 'scoredWords'>,
  word: string,
) {
  return !state.playgroundSettings.allowRepeats && state.scoredWords.includes(word)
}

export function isBoggleSelectionPreviouslyScored(state: BoggleGameState) {
  if (state.selection.length === 0) {
    return false
  }

  const word = standardLexicon.getBoardWord(state.board, state.selection)
  return state.scoredWords.includes(word)
}

export function isBoggleSelectionAlreadyPlayed(state: BoggleGameState) {
  if (state.selection.length === 0) {
    return false
  }

  return isAlreadyScoredWord(
    state,
    standardLexicon.getBoardWord(state.board, state.selection),
  )
}

function createTimeoutEndState(state: BoggleGameState): BoggleGameState {
  const endAtMs = state.intervalDeadlineMs ?? state.timeMs
  const lastResolution = state.lastResolution
  const preserveLastResolution =
    lastResolution !== null &&
    endAtMs < lastResolution.startedAtMs + lastResolution.durationMs
  const runLedger = appendRunEndLedger(state, endAtMs, 'timeout')

  return {
    ...state,
    activeSkill: null,
    endReason: 'timeout',
    isInOvertime: false,
    feedback: {
      kind: 'success',
      message: 'Time is up. Start another run to keep testing.',
      untilMs: null,
    },
    intervalDeadlineMs: state.intervalDeadlineMs === null ? null : endAtMs,
    intervalPlayBeginsAtMs: null,
    lastAction: {
      atMs: endAtMs,
      kind: 'end',
      message: 'Time is up.',
      word: null,
    },
    lastResolution: preserveLastResolution ? lastResolution : null,
    overtimeBackspaceCount: 0,
    overtimePauseUntilMs: null,
    overtimeRemaining: null,
    overtimeVelocity: 0,
    pendingIntervalTimeBonusMs: 0,
    phase: 'ended',
    runLedger,
    selection: [],
    selectionMode: null,
    skillSelection: null,
    timeMs: endAtMs,
  }
}

function usesTimerEndGoldRush(state: Pick<BoggleGameState, 'playgroundSettings'>) {
  const goldRushBurst = findGoldRushBurst(state.playgroundSettings.meter)
  return (
    state.playgroundSettings.meter.enabled &&
    goldRushBurst !== null &&
    goldRushBurst.trigger === 'timer-end'
  )
}

function isTimerEndGoldRushActive(
  state: Pick<BoggleGameState, 'meter' | 'playgroundSettings'>,
) {
  return (
    usesTimerEndGoldRush(state) &&
    isGoldRushBurstActive(state.playgroundSettings.meter, state.meter)
  )
}

function isGoldRushOvertimeState(
  state: Pick<BoggleGameState, 'isInOvertime' | 'meter' | 'playgroundSettings'>,
) {
  return state.isInOvertime && isTimerEndGoldRushActive(state)
}

function shouldStartTimerEndGoldRush(state: BoggleGameState) {
  return (
    usesTimerEndGoldRush(state) &&
    state.intervalDurationMs !== null &&
    state.intervalsRemaining !== null &&
    state.intervalsRemaining <= 1 &&
    !isGoldRushBurstActive(state.playgroundSettings.meter, state.meter)
  )
}

function getTimerEndGoldRushMeterTimeRemainingMs(state: BoggleGameState) {
  if (!isTimerEndGoldRushActive(state) || state.isInOvertime) {
    return null
  }

  if (state.meter.value <= 0) {
    return 0
  }

  const goldRushBurst = findGoldRushBurst(state.playgroundSettings.meter)
  if (goldRushBurst === null) {
    return null
  }

  const decayPerSecond = getMeterBurstMaxDecayPerSecond(
    state.playgroundSettings.meter.capacity,
    goldRushBurst.drain,
  )
  const rampMs = getMeterBurstDrainRampMs(goldRushBurst.drain)

  return decayPerSecond <= 0
    ? null
    : getMeterBurstDrainTimeRemainingMs(
        state.meter.value,
        state.meter.burstElapsedMs,
        decayPerSecond,
        rampMs,
      )
}

function enterTimerEndGoldRush(state: BoggleGameState): BoggleGameState {
  const goldRushBurst = findGoldRushBurst(state.playgroundSettings.meter)
  if (goldRushBurst === null || goldRushBurst.trigger !== 'timer-end') {
    return state
  }

  const burstState: BoggleGameState = {
    ...state,
    activeSkill: null,
    feedback: null,
    intervalDeadlineMs: null,
    intervalPlayBeginsAtMs: null,
    meter: {
      ...state.meter,
      activeBurstId: goldRushBurst.id,
      burstActive: true,
      burstElapsedMs: 0,
      overmeterValue: 0,
      value: Math.min(
        state.meter.value,
        state.playgroundSettings.meter.capacity,
      ),
    },
    pendingIntervalTimeBonusMs: 0,
    skillSelection: null,
  }
  const withGoldRushStart = appendPhaseStartLedger(burstState, 'meter-burst')

  return withGoldRushStart.meter.value <= 0
    ? enterOvertime(withGoldRushStart)
    : withGoldRushStart
}

function canRunMeterTileSpawn(
  state: Pick<BoggleGameState, 'isInOvertime' | 'meter' | 'phase' | 'playgroundSettings'>,
) {
  const { capacity, enabled, tileSpawn } = state.playgroundSettings.meter
  return (
    state.phase === 'playing' &&
    enabled &&
    tileSpawn.enabled &&
    getBoggleMeterDisplayedChargeRatio(state.meter, capacity) > 0 &&
    !isGoldRushBurstActive(state.playgroundSettings.meter, state.meter) &&
    !isMeterBurstEffectActive(
      state.playgroundSettings.meter,
      state.meter,
      'pause-tile-spawn',
    ) &&
    !state.isInOvertime
  )
}

function getStandardTilePositions(board: BoggleBoard, dimensions: GridDimensions) {
  const positions: BogglePosition[] = []

  for (let row = 0; row < dimensions.rows; row += 1) {
    for (let col = 0; col < dimensions.cols; col += 1) {
      const cell = board[row]?.[col]
      if (cell && !hasAnyBoggleSpecialTile(cell)) {
        positions.push({ col, row })
      }
    }
  }

  return positions
}

function hasMeterTileSpawnWork(state: BoggleGameState) {
  return (
    canRunMeterTileSpawn(state) &&
    getStandardTilePositions(state.board, getStateDimensions(state)).length > 0
  )
}

function applyMeterTileSpawns(
  state: BoggleGameState,
  elapsedMs: number,
  meter: BoggleGameState['meter'],
) {
  if (!canRunMeterTileSpawn({ ...state, meter }) || elapsedMs <= 0) {
    return {
      board: state.board,
      meter:
        meter.tileSpawnElapsedMs === 0
          ? meter
          : { ...meter, tileSpawnElapsedMs: 0 },
      rngState: state.rngState,
    }
  }

  const conversionIntervalMs = getBoggleMeterTileSpawnIntervalMs(
    meter,
    state.playgroundSettings.meter,
  )
  if (conversionIntervalMs === null) {
    return {
      board: state.board,
      meter:
        meter.tileSpawnElapsedMs === 0
          ? meter
          : { ...meter, tileSpawnElapsedMs: 0 },
      rngState: state.rngState,
    }
  }

  const tileKind = state.playgroundSettings.meter.tileSpawn.tileKind
  let elapsed = meter.tileSpawnElapsedMs + elapsedMs
  let nextBoard = state.board
  let nextRngState = state.rngState

  while (elapsed >= conversionIntervalMs) {
    const standardPositions = getStandardTilePositions(
      nextBoard,
      getStateDimensions(state),
    )
    if (standardPositions.length === 0) {
      elapsed = Math.min(elapsed, conversionIntervalMs)
      break
    }

    const picked = nextInteger(nextRngState, standardPositions.length)
    nextRngState = picked.rngState

    if (nextBoard === state.board) {
      nextBoard = state.board.map(row => [...row])
    }

    addSpecialTileTagAtPosition(
      nextBoard,
      [],
      standardPositions[picked.value]!,
      tileKind,
    )
    elapsed -= conversionIntervalMs
  }

  return {
    board: nextBoard,
    meter: {
      ...meter,
      tileSpawnElapsedMs: elapsed,
    },
    rngState: nextRngState,
  }
}

const BOGGLE_PLAYABILITY_DEBUG =
  typeof import.meta !== 'undefined' && Boolean((import.meta as ImportMeta).env?.DEV)

function logBoardPlayabilityRetries(
  source: string,
  attemptsUsed: number,
  options: { maxAttempts: number; ok: boolean },
) {
  if (!BOGGLE_PLAYABILITY_DEBUG) {
    return
  }

  // Remove this block once you no longer need to measure reliance on playable-board retries.
  console.log('[boggle:board-playability-retries]', {
    attemptsUsed,
    exhausted: !options.ok && attemptsUsed >= options.maxAttempts,
    maxAttempts: options.maxAttempts,
    ok: options.ok,
    source,
  })
}

function getOpeningVowelPlacement(
  letterWeights: ReadonlyArray<LetterWeight>,
  resolvedChallenge: BoggleGameState['resolvedChallenge'],
) {
  const classified = partitionLetterWeights(letterWeights)
  const band = getResolvedVowelRatioBand(resolvedChallenge)

  return {
    classified,
    opening: {
      consonantWeights: classified.consonants,
      maxVowelRatioExclusive: band.maxVowelRatioExclusive,
      minVowelRatio: band.minVowelRatio,
      vowelWeights: classified.vowels,
    },
  }
}

function createFilledLetterBoardForNewGameLayout(
  dimensions: GridDimensions,
  rngState: number,
  nextCellId: number,
  letterBags: BoggleLetterBags,
  letterWeights: ReadonlyArray<LetterWeight>,
  resolvedChallenge: BoggleGameState['resolvedChallenge'],
) {
  const { classified, opening } = getOpeningVowelPlacement(letterWeights, resolvedChallenge)

  return fillLetterBoardFromBags(
    dimensions,
    rngState,
    nextCellId,
    letterBags,
    classified,
    opening,
  )
}

function ensurePlayableBoard(
  board: BoggleBoard,
  {
    cols,
    letterBags,
    nextCellId,
    rngState,
    rows,
  }: Pick<BoggleGameState, 'cols' | 'letterBags' | 'rows' | 'nextCellId' | 'rngState'>,
  letterWeights: ReadonlyArray<LetterWeight>,
  resolvedChallenge: BoggleGameState['resolvedChallenge'],
) {
  let currentBoard = board
  let currentRngState = rngState
  let currentCellId = nextCellId
  let currentLetterBags = letterBags
  const maxAttempts = 128

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    if (isValidOpeningBoard(currentBoard, resolvedChallenge)) {
      logBoardPlayabilityRetries('ensurePlayableBoard', attempt + 1, {
        maxAttempts,
        ok: true,
      })
      return {
        board: currentBoard,
        letterBags: currentLetterBags,
        nextCellId: currentCellId,
        rngState: currentRngState,
      }
    }

    const bagSnapshot = cloneBoggleLetterBags(currentLetterBags)
    const nextBoard = createFilledLetterBoardForNewGameLayout(
      { cols, rows },
      currentRngState,
      currentCellId,
      currentLetterBags,
      letterWeights,
      resolvedChallenge,
    )
    currentBoard = nextBoard.board
    currentCellId = nextBoard.nextCellId
    currentRngState = nextBoard.rngState
    currentLetterBags = nextBoard.letterBags

    if (!isValidOpeningBoard(currentBoard, resolvedChallenge)) {
      currentLetterBags = bagSnapshot
    }
  }

  logBoardPlayabilityRetries('ensurePlayableBoard', maxAttempts, {
    maxAttempts,
    ok: false,
  })
  return {
    board: currentBoard,
    letterBags: currentLetterBags,
    nextCellId: currentCellId,
    rngState: currentRngState,
  }
}

function createPlayableBoard(
  settings: BogglePlaygroundSettings,
  rngState: number,
  nextCellId: number,
  resolvedChallenge: BoggleGameState['resolvedChallenge'] = null,
  {
    applyOpeningSpecialTiles: shouldApplyOpeningSpecialTiles = false,
  }: {
    applyOpeningSpecialTiles?: boolean
  } = {},
) {
  const dimensions = getBoggleGridDimensions(settings.gridSize)
  const letterWeights = getResolvedLetterWeights(resolvedChallenge)
  const { classified } = getOpeningVowelPlacement(letterWeights, resolvedChallenge)
  const createdBags = createBoggleLetterBags(classified, rngState)
  const initial = createFilledLetterBoardForNewGameLayout(
    dimensions,
    createdBags.rngState,
    nextCellId,
    createdBags.bags,
    letterWeights,
    resolvedChallenge,
  )
  const playableBoard = ensurePlayableBoard(
    initial.board,
    {
      cols: dimensions.cols,
      letterBags: initial.letterBags,
      nextCellId: initial.nextCellId,
      rows: dimensions.rows,
      rngState: initial.rngState,
    },
    letterWeights,
    resolvedChallenge,
  )
  if (!shouldApplyOpeningSpecialTiles) {
    return {
      board: playableBoard.board,
      letterBags: playableBoard.letterBags,
      nextCellId: playableBoard.nextCellId,
      rngState: playableBoard.rngState,
    }
  }

  const withSpecialTiles = applyOpeningSpecialTiles(
    playableBoard.board,
    dimensions,
    settings,
    playableBoard.rngState,
  )

  return {
    board: withSpecialTiles.board,
    letterBags: playableBoard.letterBags,
    nextCellId: playableBoard.nextCellId,
    rngState: withSpecialTiles.rngState,
  }
}

function getBoardSpecialTileCounts(board: BoggleBoard) {
  return {
    blue: countBoggleSpecialTilesOnBoard(board, 'blue'),
    gold: countBoggleSpecialTilesOnBoard(board, 'gold'),
    purple: countBoggleSpecialTilesOnBoard(board, 'purple'),
  } satisfies Record<BoggleSpecialTileKind, number>
}

function applyBoardSpecialTileCounts(
  board: BoggleBoard,
  dimensions: GridDimensions,
  counts: Record<BoggleSpecialTileKind, number>,
  rngState: number,
) {
  let nextBoard = board
  let nextRngState = rngState
  const filledPositions = getFilledPositions(board, dimensions)

  for (const kind of BOGGLE_SPECIAL_TILE_KINDS) {
    const count = counts[kind]
    if (count <= 0) {
      continue
    }

    const placement = placeBoggleSpecialTilesOnBoard(
      nextBoard,
      filledPositions,
      count,
      kind,
      nextRngState,
    )
    nextBoard = placement.board
    nextRngState = placement.rngState
  }

  return {
    board: nextBoard,
    rngState: nextRngState,
  }
}

function mergeNullHoleMaskFromBoard(
  holePattern: BoggleBoard,
  filledBoard: BoggleBoard,
): BoggleBoard {
  return filledBoard.map((row, r) =>
    row.map((cell, c) => (holePattern[r]?.[c] === null ? null : cell)),
  )
}

function createResetRerolledBoard(
  state: Pick<
    BoggleGameState,
    | 'allowDiagonals'
    | 'board'
    | 'cols'
    | 'letterAdjacencyMode'
    | 'letterBags'
    | 'minWordLength'
    | 'nextCellId'
    | 'playgroundSettings'
    | 'repeatTileMinGap'
    | 'resolvedChallenge'
    | 'rngState'
    | 'rows'
  >,
) {
  const dimensions = getStateDimensions(state)
  const specialTileCounts = getBoardSpecialTileCounts(state.board)
  const letterWeights = getResolvedLetterWeights(state.resolvedChallenge)
  let currentBoard = state.board
  let currentCellId = state.nextCellId
  let currentRngState = state.rngState
  let currentLetterBags = state.letterBags
  const resetMaxAttempts = 128
  let resetOk = false
  let resetAttemptsUsed = resetMaxAttempts

  for (let attempt = 0; attempt < resetMaxAttempts; attempt += 1) {
    const bagSnapshot = cloneBoggleLetterBags(currentLetterBags)
    const rolled = createFilledLetterBoardForNewGameLayout(
      dimensions,
      currentRngState,
      currentCellId,
      currentLetterBags,
      letterWeights,
      state.resolvedChallenge,
    )
    currentCellId = rolled.nextCellId
    currentRngState = rolled.rngState
    currentLetterBags = rolled.letterBags

    // When letters repopulate after words, empty slots are usually transient; keep
    // the current hole mask so reset rerolls only occupied cells (custom shapes).
    // When they do not (e.g. Reboot), cleared cells stay empty until reset — reroll
    // must refill the whole grid.
    currentBoard = state.playgroundSettings.lettersRepopulate
      ? mergeNullHoleMaskFromBoard(state.board, rolled.board)
      : rolled.board

    if (isValidOpeningBoard(currentBoard, state.resolvedChallenge)) {
      resetOk = true
      resetAttemptsUsed = attempt + 1
      break
    }

    currentLetterBags = bagSnapshot
  }

  logBoardPlayabilityRetries('createResetRerolledBoard', resetAttemptsUsed, {
    maxAttempts: resetMaxAttempts,
    ok: resetOk,
  })

  const withSpecialTiles = applyBoardSpecialTileCounts(
    currentBoard,
    dimensions,
    specialTileCounts,
    currentRngState,
  )

  return {
    board: withSpecialTiles.board,
    letterBags: currentLetterBags,
    nextCellId: currentCellId,
    rngState: withSpecialTiles.rngState,
  }
}

function uniqueRecentWords(words: string[], nextWord: string) {
  return [nextWord, ...words.filter(word => word !== nextWord)].slice(0, 8)
}

function countUniquePathCells(
  board: BoggleBoard,
  positions: readonly BogglePosition[],
) {
  const seen = new Set<string>()

  for (const position of positions) {
    const cell = board[position.row]?.[position.col]
    const key = cell?.id ?? `${position.row}:${position.col}`
    seen.add(String(key))
  }

  return seen.size
}

function getExtraLetterCount(word: string, scoreBonusMaxLength: null | number) {
  if (scoreBonusMaxLength === null) {
    return 0
  }

  return Math.max(0, word.length - scoreBonusMaxLength)
}

function getExtraLetterBonusTimeMs(
  word: string,
  settings: Pick<
    BogglePlaygroundSettings,
    'extraLetterBonusTime' | 'intervalCount' | 'scoreBonusMaxLength'
  >,
) {
  if (settings.scoreBonusMaxLength === null || settings.intervalCount === null) {
    return 0
  }

  if (settings.extraLetterBonusTime <= 0) {
    return 0
  }

  return (
    getExtraLetterCount(word, settings.scoreBonusMaxLength) *
    settings.extraLetterBonusTime *
    1000
  )
}

function applyMeterDeltaForEvent(state: BoggleGameState, delta: number): BoggleGameState {
  const meter = applyBoggleMeterDelta(state.meter, delta, state.playgroundSettings.meter)
  return meter === state.meter ? state : { ...state, meter }
}

function getFilledPositions(board: BoggleBoard, dimensions: GridDimensions) {
  const positions: BogglePosition[] = []

  for (let row = 0; row < dimensions.rows; row += 1) {
    for (let col = 0; col < dimensions.cols; col += 1) {
      if (board[row]?.[col] === null) {
        continue
      }

      positions.push({ col, row })
    }
  }

  return positions
}

function getSpecialTileSettings(
  settings: Pick<BogglePlaygroundSettings, 'specialTiles'>,
  kind: BoggleSpecialTileKind,
) {
  return settings.specialTiles[kind]
}

function getUniquePositions(positions: readonly BogglePosition[]) {
  const unique: BogglePosition[] = []
  const seen = new Set<string>()

  for (const position of positions) {
    const key = `${position.row}:${position.col}`
    if (seen.has(key)) {
      continue
    }

    seen.add(key)
    unique.push(position)
  }

  return unique
}

function findBoardPositionByCellId(board: BoggleBoard, cellId: number) {
  for (let row = 0; row < board.length; row += 1) {
    for (let col = 0; col < (board[row]?.length ?? 0); col += 1) {
      if (board[row]?.[col]?.id === cellId) {
        return { col, row }
      }
    }
  }

  return null
}

function getOrthogonalAdjacentPositions(
  origin: BogglePosition,
  dimensions: GridDimensions,
) {
  return [
    { col: origin.col, row: origin.row - 1 },
    { col: origin.col + 1, row: origin.row },
    { col: origin.col, row: origin.row + 1 },
    { col: origin.col - 1, row: origin.row },
  ].filter(
    position =>
      position.col >= 0 &&
      position.col < dimensions.cols &&
      position.row >= 0 &&
      position.row < dimensions.rows,
  )
}

function addSpecialTileTagAtPosition(
  board: BoggleBoard,
  spawned: BoggleAnimatedCell[],
  position: BogglePosition,
  kind: BoggleSpecialTileKind,
) {
  const cell = board[position.row]?.[position.col]
  if (!cell || hasAnyBoggleSpecialTile(cell)) {
    return false
  }

  board[position.row]![position.col] = {
    ...cell,
    tags: [...(cell.tags ?? []), kind],
  }
  const spawnedIndex = spawned.findIndex(
    entry => entry.toCol === position.col && entry.toRow === position.row,
  )

  if (spawnedIndex !== -1) {
    const spawnedCell = spawned[spawnedIndex]!
    spawned[spawnedIndex] = {
      ...spawnedCell,
      tags: [...(spawnedCell.tags ?? []), kind],
    }
  }

  return true
}

function removeSpecialTileTagAtPosition(
  board: BoggleBoard,
  spawned: BoggleAnimatedCell[],
  position: BogglePosition,
  kind: BoggleSpecialTileKind,
) {
  const cell = board[position.row]?.[position.col]
  if (!cell?.tags?.includes(kind)) {
    return false
  }

  const nextTags = cell.tags.filter(tag => tag !== kind)
  board[position.row]![position.col] = {
    ...cell,
    tags: nextTags.length > 0 ? nextTags : undefined,
  }

  const spawnedIndex = spawned.findIndex(
    entry => entry.toCol === position.col && entry.toRow === position.row,
  )

  if (spawnedIndex !== -1) {
    const spawnedCell = spawned[spawnedIndex]!
    const spawnedTags = (spawnedCell.tags ?? []).filter(tag => tag !== kind)
    spawned[spawnedIndex] = {
      ...spawnedCell,
      tags: spawnedTags.length > 0 ? spawnedTags : undefined,
    }
  }

  return true
}

type SubmittedWordSpecialTileTriggers = {
  clearOnUse: Array<{
    kind: BoggleSpecialTileKind
    origin: BogglePosition
  }>
  clearOnDisuse: Array<{
    cellId: number
    kind: BoggleSpecialTileKind
    origin: BogglePosition
  }>
  spreadOnDisuse: Array<{
    cellId: number
    kind: BoggleSpecialTileKind
    origin: BogglePosition
  }>
  spreadOnUse: Array<{
    kind: BoggleSpecialTileKind
    origin: BogglePosition
  }>
}

function createSubmittedWordSpecialTileTriggers(
  state: BoggleGameState,
  positions: readonly BogglePosition[],
): SubmittedWordSpecialTileTriggers {
  const usedPositions = getUniquePositions(positions)
  const usedKeys = new Set(
    usedPositions.map(position => `${position.row}:${position.col}`),
  )
  const clearOnUse: SubmittedWordSpecialTileTriggers['clearOnUse'] = []
  const clearOnDisuse: SubmittedWordSpecialTileTriggers['clearOnDisuse'] = []
  const spreadOnUse: SubmittedWordSpecialTileTriggers['spreadOnUse'] = []
  const spreadOnDisuse: SubmittedWordSpecialTileTriggers['spreadOnDisuse'] = []

  for (const position of usedPositions) {
    const cell = state.board[position.row]?.[position.col]
    if (!cell) {
      continue
    }

    for (const kind of BOGGLE_SPECIAL_TILE_KINDS) {
      const tileSettings = getSpecialTileSettings(state.playgroundSettings, kind)
      if (tileSettings.enabled && tileSettings.clearOnUse && cell.tags?.includes(kind)) {
        clearOnUse.push({ kind, origin: position })
      }

      if (
        tileSettings.enabled &&
        tileSettings.spreadToAllAdjacentOnUse &&
        cell.tags?.includes(kind)
      ) {
        spreadOnUse.push({ kind, origin: position })
      }
    }
  }

  for (let row = 0; row < state.rows; row += 1) {
    for (let col = 0; col < state.cols; col += 1) {
      if (usedKeys.has(`${row}:${col}`)) {
        continue
      }

      const cell = state.board[row]?.[col]
      if (!cell) {
        continue
      }

      for (const kind of BOGGLE_SPECIAL_TILE_KINDS) {
        const tileSettings = getSpecialTileSettings(state.playgroundSettings, kind)
        if (
          tileSettings.enabled &&
          tileSettings.clearOnDisuse &&
          cell.tags?.includes(kind)
        ) {
          clearOnDisuse.push({
            cellId: cell.id,
            kind,
            origin: { col, row },
          })
        }

        if (
          tileSettings.enabled &&
          tileSettings.spreadToOneAdjacentOnDisuse &&
          cell.tags?.includes(kind)
        ) {
          spreadOnDisuse.push({
            cellId: cell.id,
            kind,
            origin: { col, row },
          })
        }
      }
    }
  }

  return {
    clearOnUse,
    clearOnDisuse,
    spreadOnDisuse,
    spreadOnUse,
  }
}

function applyOpeningSpecialTiles(
  board: BoggleBoard,
  dimensions: GridDimensions,
  settings: BogglePlaygroundSettings,
  rngState: number,
) {
  let nextBoard = board
  let nextRngState = rngState
  const filledPositions = getFilledPositions(board, dimensions)

  for (const kind of BOGGLE_SPECIAL_TILE_KINDS) {
    const tileSettings = getSpecialTileSettings(settings, kind)
    const countAtStart = tileSettings.countAtStart ?? 0
    if (!tileSettings.enabled || countAtStart <= 0) {
      continue
    }

    const maximumCount = tileSettings.maximumCount ?? null
    const placement = placeBoggleSpecialTilesOnBoard(
      nextBoard,
      filledPositions,
      maximumCount === null ? countAtStart : Math.min(countAtStart, maximumCount),
      kind,
      nextRngState,
    )
    nextBoard = placement.board
    nextRngState = placement.rngState
  }

  return {
    board: nextBoard,
    rngState: nextRngState,
  }
}

function removeLettersWithoutGravity(
  board: BoggleBoard,
  dimensions: GridDimensions,
  positions: BogglePosition[],
) {
  const removedKeys = new Set(
    positions.map(position => `${position.row}:${position.col}`),
  )
  const nextBoard = board.map(row => [...row])
  const cleared: BoggleClearedCell[] = []

  for (let row = 0; row < dimensions.rows; row += 1) {
    for (let col = 0; col < dimensions.cols; col += 1) {
      if (!removedKeys.has(`${row}:${col}`)) {
        continue
      }

      const cell = nextBoard[row]?.[col]
      if (!cell) {
        continue
      }

      cleared.push({
        col,
        id: cell.id,
        letter: cell.letter,
        row,
        tags: cell.tags,
      })
      nextBoard[row]![col] = null
    }
  }

  return {
    board: nextBoard,
    cleared,
    moved: [] as BoggleAnimatedCell[],
  }
}

function getFilledBoardCells(board: BoggleBoard, dimensions: GridDimensions) {
  const filled: BoggleClearedCell[] = []

  for (let row = 0; row < dimensions.rows; row += 1) {
    for (let col = 0; col < dimensions.cols; col += 1) {
      const cell = board[row]?.[col]
      if (!cell) {
        continue
      }

      filled.push({
        col,
        id: cell.id,
        letter: cell.letter,
        row,
        tags: cell.tags,
      })
    }
  }

  return filled
}

function shuffleFilledBoardCells(
  board: BoggleBoard,
  dimensions: GridDimensions,
  rngState: number,
) {
  const positions: BogglePosition[] = []
  const cells: BoggleCell[] = []

  for (let row = 0; row < dimensions.rows; row += 1) {
    for (let col = 0; col < dimensions.cols; col += 1) {
      const cell = board[row]?.[col]
      if (!cell) {
        continue
      }

      positions.push({ col, row })
      cells.push(cell)
    }
  }

  if (cells.length < 2) {
    return {
      board,
      moved: [] as BoggleAnimatedCell[],
      rngState,
    }
  }

  const shuffled = [...cells]
  let currentRngState = rngState

  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const next = nextInteger(currentRngState, index + 1)
    currentRngState = next.rngState
    const swapIndex = next.value
    ;[shuffled[index], shuffled[swapIndex]] = [shuffled[swapIndex]!, shuffled[index]!]
  }

  if (shuffled.every((cell, index) => cell.id === cells[index]!.id)) {
    const offsetRoll = nextInteger(currentRngState, shuffled.length - 1)
    currentRngState = offsetRoll.rngState
    const offset = offsetRoll.value + 1
    const rotated = shuffled.slice(offset).concat(shuffled.slice(0, offset))
    shuffled.splice(0, shuffled.length, ...rotated)
  }

  const originLookup = new Map<number, BogglePosition>(
    cells.map((cell, index) => [cell.id, positions[index]!]),
  )
  const nextBoard = board.map(row => [...row])
  const moved: BoggleAnimatedCell[] = []

  for (const [index, position] of positions.entries()) {
    const cell = shuffled[index]!
    nextBoard[position.row]![position.col] = cell

    const origin = originLookup.get(cell.id)
    if (origin && (origin.row !== position.row || origin.col !== position.col)) {
      moved.push({
        col: position.col,
        fromCol: origin.col,
        fromRow: origin.row,
        id: cell.id,
        letter: cell.letter,
        tags: cell.tags,
        toCol: position.col,
        toRow: position.row,
      })
    }
  }

  return {
    board: nextBoard,
    moved,
    rngState: currentRngState,
  }
}

function repopulateBoard(
  board: BoggleBoard,
  dimensions: GridDimensions,
  rngState: number,
  nextCellId: number,
  letterBags: BoggleLetterBags,
  resolvedChallenge: BoggleGameState['resolvedChallenge'],
  {
    refillTargetStep,
    seed,
  }: {
    refillTargetStep: number
    seed: number
  },
) {
  const nextBoard = board.map(row => [...row])
  const refillLetterWeights = getResolvedRefillLetterWeights(resolvedChallenge)
  const spawned: BoggleAnimatedCell[] = []
  const spawnSlots: Array<{ col: number; fromRow: number; row: number }> = []
  let currentRngState = rngState
  let currentCellId = nextCellId

  for (let col = 0; col < dimensions.cols; col += 1) {
    let emptyIndex = 0

    for (let row = 0; row < dimensions.rows; row += 1) {
      if (nextBoard[row]?.[col] !== null) {
        continue
      }

      spawnSlots.push({
        col,
        fromRow: -1 - emptyIndex,
        row,
      })
      emptyIndex += 1
    }
  }

  if (spawnSlots.length === 0) {
    return {
      board: nextBoard,
      letterBags,
      nextCellId: currentCellId,
      rngState: currentRngState,
      spawned,
    }
  }

  const vowelBand = getResolvedVowelRatioBand(resolvedChallenge)
  const nextVowelCount = pickVowelSpawnCount(
    countBoardLetters(nextBoard),
    spawnSlots.length,
    {
      maxVowelRatioExclusive: vowelBand.maxVowelRatioExclusive,
      minVowelRatio: vowelBand.minVowelRatio,
      rngState: currentRngState,
      seed,
      step: refillTargetStep,
    },
  )
  currentRngState = nextVowelCount.rngState

  const shuffledSlotIndexes = Array.from(
    { length: spawnSlots.length },
    (_, index) => index,
  )

  for (let index = shuffledSlotIndexes.length - 1; index > 0; index -= 1) {
    const next = nextInteger(currentRngState, index + 1)
    currentRngState = next.rngState
    const swapIndex = next.value
    ;[shuffledSlotIndexes[index], shuffledSlotIndexes[swapIndex]] = [
      shuffledSlotIndexes[swapIndex]!,
      shuffledSlotIndexes[index]!,
    ]
  }

  const vowelSlotIndexes = new Set(
    shuffledSlotIndexes.slice(0, nextVowelCount.vowelSpawnCount),
  )

  let currentLetterBags = letterBags

  // Refill always writes cells back in canonical column-major order, while the
  // vowel/consonant class assignment is shuffled independently of the cleared cells.
  for (const [index, slot] of spawnSlots.entries()) {
    const letterClass = vowelSlotIndexes.has(index) ? 'vowels' : 'consonants'
    const drawn = drawLetterFromBag(
      currentLetterBags,
      letterClass,
      refillLetterWeights,
      currentRngState,
    )
    currentLetterBags = drawn.bags
    currentRngState = drawn.rngState
    const cell = {
      id: currentCellId,
      letter: drawn.letter,
      tags: undefined,
    }
    currentCellId += 1
    nextBoard[slot.row]![slot.col] = cell
    spawned.push({
      col: slot.col,
      fromCol: slot.col,
      fromRow: slot.fromRow,
      id: cell.id,
      letter: cell.letter,
      tags: cell.tags,
      toCol: slot.col,
      toRow: slot.row,
    })
  }

  return {
    board: nextBoard,
    letterBags: currentLetterBags,
    nextCellId: currentCellId,
    rngState: currentRngState,
    spawned,
  }
}

function describeBoardAftermath(state: BoggleGameState) {
  if (state.playgroundSettings.redrawBoardAfterWord) {
    return 'The whole board was cleared and a fresh layout was drawn.'
  }

  if (state.lettersRepopulate) {
    return 'Fresh letters repopulated the cleared spaces.'
  }

  return 'Cleared spaces stayed empty.'
}

function getSpawnedBoardCells(board: BoggleBoard, dimensions: GridDimensions) {
  const spawned: BoggleAnimatedCell[] = []

  for (let row = 0; row < dimensions.rows; row += 1) {
    for (let col = 0; col < dimensions.cols; col += 1) {
      const cell = board[row]?.[col]
      if (!cell) {
        continue
      }

      spawned.push({
        col,
        fromCol: col,
        fromRow: -1 - row,
        id: cell.id,
        letter: cell.letter,
        tags: cell.tags,
        toCol: col,
        toRow: row,
      })
    }
  }

  return spawned
}

function getPersistentSpecialTileSurvivorKeys(
  state: Pick<BoggleGameState, 'board' | 'playgroundSettings'>,
  consumedPositions: readonly BogglePosition[],
) {
  const consumedKeys = new Set(
    consumedPositions.map(position => `${position.row}:${position.col}`),
  )
  const survivorKeys = new Set<string>()

  for (let row = 0; row < state.board.length; row += 1) {
    for (let col = 0; col < (state.board[row]?.length ?? 0); col += 1) {
      const cell = state.board[row]?.[col]
      if (!cell || consumedKeys.has(`${row}:${col}`)) {
        continue
      }

      for (const kind of BOGGLE_SPECIAL_TILE_KINDS) {
        const tileSettings = getSpecialTileSettings(state.playgroundSettings, kind)
        if (
          tileSettings.enabled &&
          tileSettings.persistDuringBoardClears &&
          cell.tags?.includes(kind)
        ) {
          survivorKeys.add(`${row}:${col}`)
          break
        }
      }
    }
  }

  return survivorKeys
}

function annotateAnimatedSpawnTilesWithAddedSpecialTileKind(
  spawned: readonly BoggleAnimatedCell[],
  placements: readonly BogglePosition[],
  kind: BoggleSpecialTileKind,
): BoggleAnimatedCell[] {
  const touched = new Set(placements.map(position => `${position.row}:${position.col}`))

  return spawned.map(entry =>
    touched.has(`${entry.toRow}:${entry.toCol}`)
      ? {
          ...entry,
          tags: entry.tags?.includes(kind) ? entry.tags : [...(entry.tags ?? []), kind],
        }
      : entry,
  )
}

function applyBoardResetWhenSpecialTilesEmpty(
  playgroundSettings: Pick<BogglePlaygroundSettings, 'specialTiles'>,
  dimensions: GridDimensions,
  board: BoggleBoard,
  spawned: readonly BoggleAnimatedCell[],
  rngState: number,
): {
  board: BoggleBoard
  rngState: number
  spawned: BoggleAnimatedCell[]
} {
  let nextBoard = board
  let nextSpawned = [...spawned]
  let nextRngState = rngState

  for (const kind of BOGGLE_SPECIAL_TILE_KINDS) {
    const tileSettings = getSpecialTileSettings(playgroundSettings, kind)
    if (
      !tileSettings.enabled ||
      !tileSettings.resetWhenEmpty ||
      countBoggleSpecialTilesOnBoard(nextBoard, kind) !== 0
    ) {
      continue
    }

    const placementsNeeded = tileSettings.countAtStart ?? 0
    if (placementsNeeded <= 0) {
      continue
    }

    const result = placeBoggleSpecialTilesForResetWhenBoardEmpty(
      nextBoard,
      dimensions,
      kind,
      placementsNeeded,
      nextRngState,
    )
    nextBoard = result.board
    nextRngState = result.rngState
    nextSpawned = annotateAnimatedSpawnTilesWithAddedSpecialTileKind(
      nextSpawned,
      result.placements,
      kind,
    )
  }

  return {
    board: nextBoard,
    rngState: nextRngState,
    spawned: nextSpawned,
  }
}

function applySpawnedSpecialTileEffects(
  state: Pick<BoggleGameState, 'playgroundSettings' | 'specialTileSpawnCadenceCounts'>,
  board: BoggleBoard,
  cleared: BoggleClearedCell[],
  spawned: BoggleAnimatedCell[],
  rngState: number,
) {
  let nextBoard = board
  let nextSpawned = [...spawned]
  let nextRngState = rngState
  let nextCadenceCounts = { ...state.specialTileSpawnCadenceCounts }

  for (const kind of BOGGLE_SPECIAL_TILE_KINDS) {
    const tileSettings = getSpecialTileSettings(state.playgroundSettings, kind)
    const withSpawnCadence = placeBoggleSpecialTilesOnSpawnCadence<BoggleAnimatedCell>(
      nextBoard,
      nextSpawned,
      nextCadenceCounts[kind],
      tileSettings.enabled ? (tileSettings.spawnEveryXTiles ?? null) : null,
      tileSettings.enabled ? (tileSettings.maximumCount ?? null) : null,
      kind,
      nextRngState,
    )

    nextBoard = withSpawnCadence.board
    nextRngState = withSpawnCadence.rngState
    nextSpawned = withSpawnCadence.spawned
    nextCadenceCounts = {
      ...nextCadenceCounts,
      [kind]: withSpawnCadence.nextSpawnCadenceCount,
    }
  }

  for (const kind of BOGGLE_SPECIAL_TILE_KINDS) {
    const tileSettings = getSpecialTileSettings(state.playgroundSettings, kind)
    const spawnCount = tileSettings.enabled
      ? (tileSettings.spawnXTilesOnSubmit ?? null)
      : null

    if (spawnCount === null || spawnCount <= 0) {
      continue
    }

    const maximumCount = tileSettings.maximumCount ?? null
    const remainingCapacity =
      maximumCount === null
        ? spawnCount
        : Math.max(0, maximumCount - countBoggleSpecialTilesOnBoard(nextBoard, kind))

    if (remainingCapacity <= 0) {
      continue
    }

    // Guaranteed submit spawns claim newly created slots before chance rolls do,
    // and kind order determines precedence when multiple tile types compete.
    const withSubmitSpawn = placeBoggleSpecialTilesOnSpawnedCells<BoggleAnimatedCell>(
      nextBoard,
      nextSpawned,
      Math.min(spawnCount, remainingCapacity),
      kind,
      nextRngState,
    )
    nextBoard = withSubmitSpawn.board
    nextSpawned = withSubmitSpawn.spawned
    nextRngState = withSubmitSpawn.rngState
  }

  const withSpawnChance = placeBoggleSpecialTilesOnSpawnChance<BoggleAnimatedCell>(
    nextBoard,
    nextSpawned,
    Object.fromEntries(
      BOGGLE_SPECIAL_TILE_KINDS.map(kind => {
        const tileSettings = getSpecialTileSettings(state.playgroundSettings, kind)
        return [
          kind,
          {
            maximumCount: tileSettings.enabled
              ? (tileSettings.maximumCount ?? null)
              : null,
            spawnChancePercent: tileSettings.enabled
              ? (tileSettings.spawnChancePercent ?? null)
              : null,
          },
        ]
      }),
    ) as Record<
      BoggleSpecialTileKind,
      {
        maximumCount: null | number
        spawnChancePercent: null | number
      }
    >,
    nextRngState,
  )

  nextBoard = withSpawnChance.board
  nextSpawned = withSpawnChance.spawned
  nextRngState = withSpawnChance.rngState

  for (const kind of BOGGLE_SPECIAL_TILE_KINDS) {
    const tileSettings = getSpecialTileSettings(state.playgroundSettings, kind)
    const replacementsFromClears =
      tileSettings.enabled && tileSettings.replaceWhenCleared
        ? countBoggleSpecialTiles(cleared, kind)
        : 0
    const maximumCount = tileSettings.maximumCount ?? null
    const remainingCapacity =
      maximumCount === null
        ? replacementsFromClears
        : Math.max(0, maximumCount - countBoggleSpecialTilesOnBoard(nextBoard, kind))

    if (replacementsFromClears <= 0 || remainingCapacity <= 0) {
      continue
    }

    // Replace specials one-for-one on newly spawned tiles by tagging them with the
    // seeded RNG so same-settle cadence hits stay deterministic and never push
    // the board past the configured maximum for this tile kind.
    const withReplacement = placeBoggleSpecialTilesOnSpawnedCells<BoggleAnimatedCell>(
      nextBoard,
      nextSpawned,
      Math.min(replacementsFromClears, remainingCapacity),
      kind,
      nextRngState,
    )
    nextBoard = withReplacement.board
    nextSpawned = withReplacement.spawned
    nextRngState = withReplacement.rngState
  }

  return {
    board: nextBoard,
    rngState: nextRngState,
    specialTileSpawnCadenceCounts: nextCadenceCounts,
    spawned: nextSpawned,
  }
}

function applySubmittedWordSpecialTileConversions(
  state: Pick<BoggleGameState, 'playgroundSettings'>,
  resolvedBoard: {
    board: BoggleBoard
    rngState: number
    spawned: BoggleAnimatedCell[]
  },
) {
  let nextBoard = resolvedBoard.board
  let nextRngState = resolvedBoard.rngState
  let nextSpawned = [...resolvedBoard.spawned]

  for (const kind of BOGGLE_SPECIAL_TILE_KINDS) {
    const tileSettings = getSpecialTileSettings(state.playgroundSettings, kind)
    const convertCount = tileSettings.enabled
      ? (tileSettings.convertXTilesOnSubmit ?? null)
      : null

    if (convertCount === null || convertCount <= 0) {
      continue
    }

    const maximumCount = tileSettings.maximumCount ?? null
    const remainingCapacity =
      maximumCount === null
        ? convertCount
        : Math.max(0, maximumCount - countBoggleSpecialTilesOnBoard(nextBoard, kind))

    if (remainingCapacity <= 0) {
      continue
    }

    const eligiblePositions = getFilledPositions(nextBoard, {
      cols: nextBoard[0]?.length ?? 0,
      rows: nextBoard.length,
    }).filter(
      position => !hasAnyBoggleSpecialTile(nextBoard[position.row]?.[position.col]),
    )

    if (eligiblePositions.length === 0) {
      continue
    }

    const conversion = placeBoggleSpecialTilesOnBoard(
      nextBoard,
      eligiblePositions,
      Math.min(convertCount, remainingCapacity),
      kind,
      nextRngState,
    )
    const selectedPositions = new Set(
      conversion.placements.map(position => `${position.row}:${position.col}`),
    )

    nextBoard = conversion.board
    nextRngState = conversion.rngState
    nextSpawned = nextSpawned.map(entry =>
      selectedPositions.has(`${entry.toRow}:${entry.toCol}`)
        ? {
            ...entry,
            tags: [...(entry.tags ?? []), kind],
          }
        : entry,
    )
  }

  return {
    board: nextBoard,
    rngState: nextRngState,
    spawned: nextSpawned,
  }
}

function applyPersistentSpecialTilesAfterClear(
  state: Pick<BoggleGameState, 'playgroundSettings'>,
  resolvedBoard: {
    board: BoggleBoard
    cleared: BoggleClearedCell[]
    rngState: number
    spawned: BoggleAnimatedCell[]
  },
  consumedPositions: readonly BogglePosition[],
) {
  const nextBoard = resolvedBoard.board.map(row => [...row])
  const nextSpawned = [...resolvedBoard.spawned]
  const consumedKeys = new Set(
    consumedPositions.map(position => `${position.row}:${position.col}`),
  )
  let changed = false

  for (const clearedCell of resolvedBoard.cleared) {
    if (consumedKeys.has(`${clearedCell.row}:${clearedCell.col}`)) {
      continue
    }

    for (const kind of BOGGLE_SPECIAL_TILE_KINDS) {
      const tileSettings = getSpecialTileSettings(state.playgroundSettings, kind)
      if (
        !tileSettings.enabled ||
        !tileSettings.persistDuringBoardClears ||
        !clearedCell.tags?.includes(kind)
      ) {
        continue
      }

      if (
        addSpecialTileTagAtPosition(
          nextBoard,
          nextSpawned,
          { col: clearedCell.col, row: clearedCell.row },
          kind,
        )
      ) {
        changed = true
      }
    }
  }

  return changed
    ? {
        board: nextBoard,
        rngState: resolvedBoard.rngState,
        spawned: nextSpawned,
      }
    : {
        board: resolvedBoard.board,
        rngState: resolvedBoard.rngState,
        spawned: resolvedBoard.spawned,
      }
}

function applySubmittedWordSpecialTileSpread(
  state: Pick<BoggleGameState, 'playgroundSettings' | 'rows' | 'cols'>,
  resolvedBoard: {
    board: BoggleBoard
    rngState: number
    spawned: BoggleAnimatedCell[]
  },
  triggers: SubmittedWordSpecialTileTriggers,
) {
  const nextBoard = resolvedBoard.board.map(row => [...row])
  const nextSpawned = [...resolvedBoard.spawned]
  const dimensions = {
    cols: state.cols,
    rows: state.rows,
  }
  let changed = false
  let nextRngState = resolvedBoard.rngState

  function getCurrentTriggerPosition(trigger: {
    cellId: number
    kind: BoggleSpecialTileKind
    origin: BogglePosition
  }) {
    return (
      findBoardPositionByCellId(nextBoard, trigger.cellId) ??
      (nextBoard[trigger.origin.row]?.[trigger.origin.col]?.tags?.includes(trigger.kind)
        ? trigger.origin
        : null)
    )
  }

  function getRemainingCapacity(kind: BoggleSpecialTileKind) {
    const maximumCount = getSpecialTileSettings(
      state.playgroundSettings,
      kind,
    ).maximumCount

    return maximumCount === null
      ? Number.POSITIVE_INFINITY
      : Math.max(0, (maximumCount ?? 0) - countBoggleSpecialTilesOnBoard(nextBoard, kind))
  }

  for (const trigger of triggers.spreadOnUse) {
    if (
      removeSpecialTileTagAtPosition(nextBoard, nextSpawned, trigger.origin, trigger.kind)
    ) {
      changed = true
    }

    // Convert the repopulated letter where the contagious tile was played, then each
    // orthogonal neighbor: strip other special-tile kinds so cadence/refill tags cannot
    // block contagion; do not cap by maximumCount here.
    if (
      forceContagionSpreadKindOnNeighbor(
        nextBoard,
        nextSpawned,
        trigger.origin,
        trigger.kind,
      )
    ) {
      changed = true
    }

    for (const adjacent of getOrthogonalAdjacentPositions(trigger.origin, dimensions)) {
      if (
        forceContagionSpreadKindOnNeighbor(nextBoard, nextSpawned, adjacent, trigger.kind)
      ) {
        changed = true
      }
    }
  }

  for (const trigger of triggers.clearOnUse) {
    if (
      removeSpecialTileTagAtPosition(nextBoard, nextSpawned, trigger.origin, trigger.kind)
    ) {
      changed = true
    }
  }

  for (const trigger of triggers.clearOnDisuse) {
    const currentPosition = getCurrentTriggerPosition(trigger)
    if (currentPosition === null) {
      continue
    }

    if (
      removeSpecialTileTagAtPosition(
        nextBoard,
        nextSpawned,
        currentPosition,
        trigger.kind,
      )
    ) {
      changed = true
    }
  }

  for (const trigger of triggers.spreadOnDisuse) {
    const currentPosition = getCurrentTriggerPosition(trigger)
    if (currentPosition === null) {
      continue
    }

    const eligibleAdjacent = getOrthogonalAdjacentPositions(
      currentPosition,
      dimensions,
    ).filter(position => {
      const cell = nextBoard[position.row]?.[position.col]
      return cell !== null && cell !== undefined && !hasAnyBoggleSpecialTile(cell)
    })

    if (eligibleAdjacent.length === 0 || getRemainingCapacity(trigger.kind) <= 0) {
      continue
    }

    const picked = nextInteger(nextRngState, eligibleAdjacent.length)
    nextRngState = picked.rngState

    if (
      addSpecialTileTagAtPosition(
        nextBoard,
        nextSpawned,
        eligibleAdjacent[picked.value]!,
        trigger.kind,
      )
    ) {
      changed = true
    }
  }

  return changed
    ? {
        board: nextBoard,
        rngState: nextRngState,
        spawned: nextSpawned,
      }
    : resolvedBoard
}

function redrawBoardAfterAcceptedWord(
  state: BoggleGameState,
  consumedPositions: readonly BogglePosition[],
) {
  const dimensions = getStateDimensions(state)
  const persistentSurvivorKeys = getPersistentSpecialTileSurvivorKeys(
    state,
    consumedPositions,
  )
  const playable = createPlayableBoard(
    state.playgroundSettings,
    state.rngState,
    state.nextCellId,
    state.resolvedChallenge,
    {
      applyOpeningSpecialTiles: false,
    },
  )
  const nextBoard = playable.board.map(row => [...row])
  const nextSpawned = getSpawnedBoardCells(playable.board, dimensions)
  const cleared = getFilledBoardCells(state.board, dimensions).filter(
    cell => !persistentSurvivorKeys.has(`${cell.row}:${cell.col}`),
  )

  for (let row = 0; row < dimensions.rows; row += 1) {
    for (let col = 0; col < dimensions.cols; col += 1) {
      if (!persistentSurvivorKeys.has(`${row}:${col}`)) {
        continue
      }

      const previousCell = state.board[row]?.[col]
      if (!previousCell) {
        continue
      }

      for (const kind of BOGGLE_SPECIAL_TILE_KINDS) {
        const tileSettings = getSpecialTileSettings(state.playgroundSettings, kind)
        if (
          !tileSettings.enabled ||
          !tileSettings.persistDuringBoardClears ||
          !previousCell.tags?.includes(kind)
        ) {
          continue
        }

        addSpecialTileTagAtPosition(nextBoard, nextSpawned, { col, row }, kind)
      }
    }
  }

  const freshSpawned = nextSpawned.filter(
    cell => !persistentSurvivorKeys.has(`${cell.toRow}:${cell.toCol}`),
  )
  const withSpecialTiles = applySpawnedSpecialTileEffects(
    state,
    nextBoard,
    cleared,
    freshSpawned,
    playable.rngState,
  )

  return {
    board: withSpecialTiles.board,
    cleared,
    letterBags: playable.letterBags,
    moved: [] as BoggleAnimatedCell[],
    nextCellId: playable.nextCellId,
    rngState: withSpecialTiles.rngState,
    specialTileSpawnCadenceCounts: withSpecialTiles.specialTileSpawnCadenceCounts,
    spawned: withSpecialTiles.spawned,
    spawnedTileCount: state.spawnedTileCount + withSpecialTiles.spawned.length,
  }
}

function applyTileSpawnStagger(
  playgroundSettings: Pick<BogglePlaygroundSettings, 'tileSpawnIntervalMs'>,
  moved: readonly BoggleAnimatedCell[],
  spawned: readonly BoggleAnimatedCell[],
): {
  durationMs: number
  movementDurationMs?: number
  spawned: BoggleAnimatedCell[]
} {
  const spawnAnimMs = BOGGLE_RESOLVE_ANIMATION_MS
  const staggerMs = playgroundSettings.tileSpawnIntervalMs
  const hasMoved = moved.length > 0
  const hasSpawned = spawned.length > 0

  if (!hasSpawned) {
    return {
      durationMs: spawnAnimMs,
      movementDurationMs: hasMoved ? spawnAnimMs : undefined,
      spawned: [...spawned],
    }
  }

  let nextSpawned: BoggleAnimatedCell[]
  let spawnPhaseEnd = spawnAnimMs

  if (staggerMs > 0) {
    const sorted = [...spawned].sort((a, b) =>
      a.toRow !== b.toRow ? a.toRow - b.toRow : a.toCol - b.toCol,
    )
    const delayById = new Map<number, number>()
    for (let index = 0; index < sorted.length; index += 1) {
      delayById.set(sorted[index]!.id, index * staggerMs)
    }
    nextSpawned = spawned.map(cell => ({
      ...cell,
      spawnDelayMs: delayById.get(cell.id) ?? 0,
    }))
    spawnPhaseEnd = (spawned.length - 1) * staggerMs + spawnAnimMs
  } else {
    nextSpawned = spawned.map(cell => {
      const { spawnDelayMs: _omit, ...rest } = cell
      return rest
    })
  }

  const movePhaseMs = hasMoved ? spawnAnimMs : 0
  const movementDurationMs = hasMoved || hasSpawned ? spawnAnimMs : undefined
  const totalDuration = Math.max(movePhaseMs, spawnPhaseEnd)

  return {
    durationMs: Math.max(totalDuration, movementDurationMs ?? 0),
    movementDurationMs,
    spawned: nextSpawned,
  }
}

function settleBoardAfterRemoval(state: BoggleGameState, positions: BogglePosition[]) {
  if (state.playgroundSettings.redrawBoardAfterWord) {
    return redrawBoardAfterAcceptedWord(state, positions)
  }

  const dimensions = getStateDimensions(state)
  const removal = removeLettersWithoutGravity(state.board, dimensions, positions)

  if (!state.lettersRepopulate) {
    return {
      board: removal.board,
      cleared: removal.cleared,
      letterBags: state.letterBags,
      moved: removal.moved,
      nextCellId: state.nextCellId,
      rngState: state.rngState,
      specialTileSpawnCadenceCounts: state.specialTileSpawnCadenceCounts,
      spawnedTileCount: state.spawnedTileCount,
      spawned: [],
    }
  }

  const refill = repopulateBoard(
    removal.board,
    dimensions,
    state.rngState,
    state.nextCellId,
    state.letterBags,
    state.resolvedChallenge,
    {
      refillTargetStep: state.wordsCleared + state.spawnedTileCount,
      seed: state.seed,
    },
  )
  const withSpecialTiles = applySpawnedSpecialTileEffects(
    state,
    refill.board,
    removal.cleared,
    refill.spawned,
    refill.rngState,
  )

  return {
    board: withSpecialTiles.board,
    cleared: removal.cleared,
    letterBags: refill.letterBags,
    moved: removal.moved,
    nextCellId: refill.nextCellId,
    rngState: withSpecialTiles.rngState,
    specialTileSpawnCadenceCounts: withSpecialTiles.specialTileSpawnCadenceCounts,
    spawnedTileCount: state.spawnedTileCount + refill.spawned.length,
    spawned: withSpecialTiles.spawned,
  }
}

function getSkillSetting(state: BoggleGameState, mode: BoggleSkillMode) {
  return state.playgroundSettings.skills[mode]
}

function isSkillEnabled(state: BoggleGameState, mode: BoggleSkillMode) {
  return getSkillSetting(state, mode).enabled
}

function createDefaultSettings(overrides: BogglePlaygroundSettingsInput = {}) {
  return normalizeBogglePlaygroundSettings(overrides, DEFAULT_BOGGLE_PLAYGROUND_SETTINGS)
}

function createTimerReadyMessage(
  intervalCount: null | number,
  intervalTimeSeconds: number,
) {
  if (intervalCount === null) {
    return 'Timer off.'
  }

  if (intervalCount === 1) {
    return `Timer set to ${intervalTimeSeconds}s. Clear tiles before the clock runs out.`
  }

  return `${intervalCount} intervals set to ${intervalTimeSeconds}s each.`
}

function createNoPlayableWordsEndState(state: BoggleGameState): BoggleGameState {
  const runLedger = appendRunEndLedger(state, state.timeMs, 'no-words')
  return {
    ...state,
    activeSkill: null,
    endReason: 'no-words',
    feedback: {
      kind: 'success',
      message: 'No valid words remain. Start another run to keep testing.',
      untilMs: null,
    },
    intervalPlayBeginsAtMs: null,
    isInOvertime: false,
    lastAction: {
      atMs: state.timeMs,
      kind: 'end',
      message: 'No valid words remain.',
      word: null,
    },
    lastResolution: null,
    overtimeBackspaceCount: 0,
    overtimePauseUntilMs: null,
    overtimeRemaining: null,
    overtimeVelocity: 0,
    pendingIntervalTimeBonusMs: 0,
    phase: 'ended',
    runLedger,
    selection: [],
    selectionMode: null,
    skillSelection: null,
  }
}

function endRunIfNoPlayableWordsRemain(state: BoggleGameState) {
  // Runs only end when the timer runs out. An empty board or a board with no
  // available words is fine - the player can use Reset (or any other recovery
  // skill) to regenerate letters, or simply run out of time.
  return state
}

function getResetCombo(state: Pick<BoggleGameState, 'combo' | 'playgroundSettings'>) {
  return state.playgroundSettings.combo.enabled
    ? state.playgroundSettings.combo.startingValue
    : state.combo
}

function clearSelectionState(state: BoggleGameState) {
  if (
    state.selection.length === 0 &&
    state.selectionMode === null &&
    state.skillSelection === null
  ) {
    return state
  }

  return {
    ...state,
    selection: [],
    selectionMode: null,
    skillSelection: null,
    selectionAddedDuringOvertime: [],
  }
}

function createNextIntervalState(
  state: BoggleGameState,
  cause: 'clear' | 'submit' | 'timeout',
): BoggleGameState {
  const dimensions = getStateDimensions(state)
  const playable = createPlayableBoard(
    state.playgroundSettings,
    state.rngState,
    state.nextCellId,
    state.resolvedChallenge,
  )
  const cleared = getFilledBoardCells(state.board, dimensions)
  const withSpecialTiles = applySpawnedSpecialTileEffects(
    state,
    playable.board,
    cleared,
    getSpawnedBoardCells(playable.board, dimensions),
    playable.rngState,
  )
  const withResetWhenEmpty = applyBoardResetWhenSpecialTilesEmpty(
    state.playgroundSettings,
    dimensions,
    withSpecialTiles.board,
    withSpecialTiles.spawned,
    withSpecialTiles.rngState,
  )
  const intervalIndex = getIntervalIndex(state)
  const nextIntervalIndex =
    intervalIndex === null
      ? null
      : Math.min(
          intervalIndex + 1,
          state.playgroundSettings.intervalCount ?? intervalIndex + 1,
        )
  const nextIntervalTimeBonusMs = cause === 'clear' ? 0 : state.pendingIntervalTimeBonusMs

  const intervalStagger = applyTileSpawnStagger(
    state.playgroundSettings,
    [],
    withResetWhenEmpty.spawned,
  )

  return {
    ...state,
    activeSkill: null,
    board: withResetWhenEmpty.board,
    combo: getResetCombo(state),
    feedback: {
      kind: 'success',
      message:
        cause === 'clear'
          ? `Selection cleared. Interval ${nextIntervalIndex} is ready.`
          : cause === 'submit'
            ? `Interval ${nextIntervalIndex} is ready.`
            : `Interval ${intervalIndex} ended. Interval ${nextIntervalIndex} is ready.`,
      untilMs: state.timeMs + BOGGLE_INVALID_FEEDBACK_MS,
    },
    intervalDeadlineMs:
      state.intervalDurationMs === null
        ? null
        : state.timeMs + state.intervalDurationMs + nextIntervalTimeBonusMs,
    intervalPlayBeginsAtMs: null,
    intervalsRemaining:
      state.intervalsRemaining === null ? null : state.intervalsRemaining - 1,
    isInOvertime: false,
    lastAction: {
      atMs: state.timeMs,
      kind: 'interval',
      message:
        cause === 'clear'
          ? 'Selection cleared. Interval advanced.'
          : cause === 'submit'
            ? 'Accepted word ended the interval.'
            : `Interval ${intervalIndex} ended.`,
      word: null,
    },
    lastResolution: {
      cleared,
      durationMs: intervalStagger.durationMs,
      movementDurationMs: intervalStagger.movementDurationMs,
      moved: [],
      spawned: intervalStagger.spawned,
      startedAtMs: state.timeMs,
      word: nextIntervalIndex === null ? 'INTERVAL' : `INTERVAL ${nextIntervalIndex}`,
    },
    letterBags: playable.letterBags,
    nextCellId: playable.nextCellId,
    overtimeBackspaceCount: 0,
    overtimePauseUntilMs: null,
    overtimeRemaining: null,
    overtimeVelocity: 0,
    pendingIntervalTimeBonusMs: 0,
    phase: 'playing',
    rngState: withResetWhenEmpty.rngState,
    selection: [],
    selectionMode: null,
    specialTileSpawnCadenceCounts: withSpecialTiles.specialTileSpawnCadenceCounts,
    spawnedTileCount: state.spawnedTileCount + withSpecialTiles.spawned.length,
    skillSelection: null,
  }
}

function endTimedRun(
  state: BoggleGameState,
  cause: 'clear' | 'submit' | 'timeout',
): BoggleGameState {
  if (cause === 'timeout') {
    return createTimeoutEndState(state)
  }

  const endedState = createTimeoutEndState({
    ...state,
    intervalDeadlineMs: state.timeMs,
    intervalPlayBeginsAtMs: null,
    // Product question: if a blue-tile word ends the final interval there is no
    // next interval to receive the carried +2s. Assume no-op for now.
    pendingIntervalTimeBonusMs: 0,
  })

  return {
    ...endedState,
    feedback: {
      kind: 'success',
      message:
        cause === 'submit'
          ? 'Accepted word ended the final interval.'
          : 'Selection cleared. The run ended.',
      untilMs: null,
    },
    lastAction: {
      atMs: state.timeMs,
      kind: 'end',
      message:
        cause === 'submit'
          ? 'Accepted word ended the final interval.'
          : 'Selection cleared. The run ended.',
      word: null,
    },
  }
}

function resolveExpiredTimedInterval(
  state: BoggleGameState,
  cause: 'clear' | 'submit' | 'timeout' = 'timeout',
): BoggleGameState {
  if (state.intervalDurationMs === null || state.intervalsRemaining === null) {
    return cause === 'clear' ? clearSelectionState(state) : state
  }

  if (state.intervalsRemaining <= 1) {
    if (cause === 'timeout' && shouldStartTimerEndGoldRush(state)) {
      return enterTimerEndGoldRush(state)
    }

    return endTimedRun(state, cause)
  }

  return createNextIntervalState(state, cause)
}

function advanceIntervalAfterSubmission(acceptedState: BoggleGameState): BoggleGameState {
  const advancedState = resolveExpiredTimedInterval(acceptedState, 'submit')
  const intervalMessage = advancedState.feedback?.message
  const lastActionMessage =
    advancedState.phase === 'ended' ? 'Final interval complete.' : 'Interval advanced.'

  return {
    ...advancedState,
    feedback:
      acceptedState.feedback === null
        ? advancedState.feedback
        : {
            ...acceptedState.feedback,
            message: intervalMessage
              ? `${acceptedState.feedback.message} ${intervalMessage}`
              : acceptedState.feedback.message,
            untilMs: advancedState.feedback?.untilMs ?? acceptedState.feedback.untilMs,
          },
    lastAction: {
      ...acceptedState.lastAction,
      message: `${acceptedState.lastAction.message} ${lastActionMessage}`,
    },
  }
}

function resolveAcceptedTimedExpiry(acceptedState: BoggleGameState): BoggleGameState {
  const advancedState = resolveExpiredTimedInterval(acceptedState, 'timeout')
  const timeoutMessage =
    advancedState.phase === 'ended' ? 'Time is up.' : advancedState.lastAction.message

  return {
    ...advancedState,
    feedback:
      acceptedState.feedback === null
        ? advancedState.feedback
        : {
            ...acceptedState.feedback,
            message: advancedState.feedback?.message
              ? `${acceptedState.feedback.message} ${advancedState.feedback.message}`
              : acceptedState.feedback.message,
            untilMs: advancedState.feedback?.untilMs ?? acceptedState.feedback.untilMs,
          },
    lastAction: {
      ...acceptedState.lastAction,
      message: `${acceptedState.lastAction.message} ${timeoutMessage}`,
    },
  }
}

function resolveAcceptedOvertimeState(acceptedState: BoggleGameState): BoggleGameState {
  const intervalTimeRemainingMs = getIntervalTimeRemainingMs(acceptedState)

  return intervalTimeRemainingMs !== null && intervalTimeRemainingMs > 0
    ? acceptedState
    : resolveAcceptedTimedExpiry(acceptedState)
}

function enterOvertime(state: BoggleGameState): BoggleGameState {
  return appendPhaseStartLedger(
    {
      ...state,
      activeSkill: null,
      feedback: null,
      isInOvertime: true,
      overtimeBackspaceCount: 0,
      overtimePauseUntilMs: null,
      overtimeRemaining: BOGGLE_OVERTIME_START_VALUE,
      overtimeVelocity: 0,
      skillSelection: null,
    },
    'overtime',
  )
}

function applyOvertimeLetterPress(state: BoggleGameState): BoggleGameState {
  if (!state.isInOvertime) {
    return state
  }

  return {
    ...state,
    overtimePauseUntilMs: state.timeMs + BOGGLE_OVERTIME_LETTER_PRESS_PAUSE_MS,
    overtimeVelocity: 0,
  }
}

function getOvertimeBackspaceEscalationStrength(pressCount: number) {
  const chargedPressCount = Math.max(
    0,
    pressCount - BOGGLE_OVERTIME_BACKSPACE_FREE_PRESSES,
  )
  return chargedPressCount ** BOGGLE_OVERTIME_BACKSPACE_ESCALATION_EXPONENT
}

function applyOvertimeBackspacePenalty(state: BoggleGameState): BoggleGameState {
  if (!state.isInOvertime || state.overtimeRemaining === null) {
    return state
  }

  const escalation = state.overtimeBackspaceCount + 1
  const escalationStrength = getOvertimeBackspaceEscalationStrength(escalation)
  const penalty = BOGGLE_OVERTIME_BACKSPACE_PENALTY_PER_TIER * escalationStrength
  const velocityBoost = BOGGLE_OVERTIME_BACKSPACE_VELOCITY_PER_TIER * escalationStrength

  return {
    ...state,
    overtimeBackspaceCount: escalation,
    overtimePauseUntilMs: null,
    overtimeRemaining: clamp(
      state.overtimeRemaining - penalty,
      0,
      BOGGLE_OVERTIME_START_VALUE,
    ),
    overtimeVelocity: state.overtimeVelocity + velocityBoost,
  }
}

export function removeLastSelectionEntry(state: BoggleGameState) {
  if (!isPlaying(state) || state.selection.length === 0) {
    return state
  }

  const clearedSelection =
    state.selection.length === 1
      ? clearSelectionState(state)
      : {
          ...state,
          feedback: null,
          selection: state.selection.slice(0, -1),
          selectionMode: state.selectionMode,
          selectionAddedDuringOvertime: state.selectionAddedDuringOvertime.slice(0, -1),
        }

  const withBackspace = {
    ...clearedSelection,
    hadBackspaceWhileFormingWord: true,
    runLedger: [
      ...clearedSelection.runLedger,
      {
        kind: 'correction' as const,
        correctionKind: 'backspace' as const,
        index: clearedSelection.runLedger.length,
        atMs: clearedSelection.timeMs,
      },
    ],
  }

  const withMeterPenalty = applyMeterDeltaForEvent(
    withBackspace,
    -withBackspace.playgroundSettings.meter.decrease.backspace,
  )

  if (!withMeterPenalty.isInOvertime) {
    return withMeterPenalty
  }

  const punishedState = applyOvertimeBackspacePenalty(withMeterPenalty)

  return punishedState.overtimeRemaining !== null && punishedState.overtimeRemaining <= 0
    ? resolveOvertimeExpiry(punishedState)
    : punishedState
}

function createInvalidState(
  state: BoggleGameState,
  message: string,
  word: string,
  {
    activeSkill = state.activeSkill,
    selection = [],
    skillSelection = state.skillSelection,
    selectionMode = selection.length === 0 ? null : state.selectionMode,
  }: {
    activeSkill?: BoggleGameState['activeSkill']
    selection?: BogglePosition[]
    selectionMode?: BoggleSelectionMode | null
    skillSelection?: BoggleSkillSelection | null
  } = {},
): BoggleGameState {
  return {
    ...state,
    activeSkill,
    feedback: {
      kind: 'invalid',
      message,
      untilMs: state.timeMs + BOGGLE_INVALID_FEEDBACK_MS,
    },
    lastAction: {
      atMs: state.timeMs,
      kind: 'invalid',
      message,
      word: word || null,
    },
    selection,
    selectionMode,
    skillSelection,
  }
}

type BlueTileTimeBonusMode = 'apply-now' | 'carry-to-next-interval' | 'discard'

function getBlueTileTimeBonusModeForAcceptedWord(
  state: Pick<
    BoggleGameState,
    'intervalDurationMs' | 'intervalsRemaining' | 'playgroundSettings'
  >,
  advanceTrigger: 'submit' | 'timeout',
): BlueTileTimeBonusMode {
  if (state.intervalDurationMs === null) {
    return 'discard'
  }

  if (advanceTrigger === 'submit') {
    if (!state.playgroundSettings.endIntervalOnSubmit) {
      return 'apply-now'
    }

    return (state.intervalsRemaining ?? 0) > 1 ? 'carry-to-next-interval' : 'discard'
  }

  return (state.intervalsRemaining ?? 0) > 1 ? 'carry-to-next-interval' : 'discard'
}

function getAcceptedWordSpecialTileOutcome(
  state: BoggleGameState,
  positions: readonly BogglePosition[],
) {
  const pathSteps = createAcceptedWordPathSteps(state, positions)
  const physicalSpecialTileCountsUsed = buildPhysicalSpecialTileCounts(pathSteps)
  const effectiveSpecialTileCountsUsed = buildEffectiveSpecialTileCounts(pathSteps)
  const goldInPath = getEffectiveGoldCount(effectiveSpecialTileCountsUsed)

  return {
    blueTimeBonusMs: getBoggleSpecialTileTimeBonusMs(state.board, positions),
    effectiveSpecialTileCountsUsed,
    goldInPath,
    pathSteps,
    physicalSpecialTileCountsUsed,
  }
}

function createAcceptedWordPathSteps(
  state: BoggleGameState,
  positions: readonly BogglePosition[],
): BoggleRunLedgerAcceptedWordPathStep[] {
  const goldRushActive = isBoggleGoldRushActive(state)
  return positions.map(position => {
    const cell = state.board[position.row]?.[position.col]
    const physicalSpecialKinds = BOGGLE_SPECIAL_TILE_KINDS.filter(kind =>
      cell?.tags?.includes(kind),
    )
    const virtualSpecialKinds: BoggleSpecialTileKind[] =
      goldRushActive &&
      cell &&
      !physicalSpecialKinds.includes(BOGGLE_GOLD_TILE_KIND)
        ? [BOGGLE_GOLD_TILE_KIND]
        : []
    const effectiveSpecialKinds = [
      ...new Set([...physicalSpecialKinds, ...virtualSpecialKinds]),
    ]

    return {
      effectiveSpecialKinds,
      letter: cell?.letter ?? '',
      physicalSpecialKinds,
      position: { ...position },
      virtualSpecialKinds,
    }
  })
}

function applyWordClear(
  state: BoggleGameState,
  positions: BogglePosition[],
  word: string,
  {
    blueTileTimeBonusMode = 'apply-now',
    preserveOvertime = false,
    skipNoPlayableWordsCheck = false,
    suppressMeterIncrease = false,
  }: {
    blueTileTimeBonusMode?: BlueTileTimeBonusMode
    preserveOvertime?: boolean
    skipNoPlayableWordsCheck?: boolean
    suppressMeterIncrease?: boolean
  } = {},
): BoggleGameState {
  const specialTileTriggers = createSubmittedWordSpecialTileTriggers(state, positions)
  const settledBoard = settleBoardAfterRemoval(state, positions)
  const persistedBoard = {
    ...settledBoard,
    ...applyPersistentSpecialTilesAfterClear(state, settledBoard, positions),
  }
  const convertedBoard = {
    ...persistedBoard,
    ...applySubmittedWordSpecialTileConversions(state, persistedBoard),
  }
  const afterSpread = {
    ...convertedBoard,
    ...applySubmittedWordSpecialTileSpread(state, convertedBoard, specialTileTriggers),
  }
  const settleDimensions = getStateDimensions(state)
  const afterResetWhenEmptyBoard = applyBoardResetWhenSpecialTilesEmpty(
    state.playgroundSettings,
    settleDimensions,
    afterSpread.board,
    afterSpread.spawned,
    afterSpread.rngState,
  )
  const resolvedBoard = {
    ...afterSpread,
    board: afterResetWhenEmptyBoard.board,
    rngState: afterResetWhenEmptyBoard.rngState,
    spawned: afterResetWhenEmptyBoard.spawned,
  }
  const lettersRemainingAfterWord = getFilledPositions(
    resolvedBoard.board,
    getStateDimensions(state),
  ).length
  const boardClearedAfterWord = lettersRemainingAfterWord === 0
  // When a word empties the board, repopulate immediately using the Reset
  // skill's rerolled-board logic so the player can keep playing.
  const repopulated = boardClearedAfterWord
    ? (() => {
        const rerolled = createResetRerolledBoard({
          ...state,
          board: resolvedBoard.board,
          nextCellId: resolvedBoard.nextCellId,
          rngState: resolvedBoard.rngState,
        })
        return {
          board: rerolled.board,
          letterBags: rerolled.letterBags,
          nextCellId: rerolled.nextCellId,
          rngState: rerolled.rngState,
          spawned: [
            ...resolvedBoard.spawned,
            ...getSpawnedBoardCells(rerolled.board, getStateDimensions(state)),
          ],
        }
      })()
    : {
        board: resolvedBoard.board,
        letterBags: resolvedBoard.letterBags,
        nextCellId: resolvedBoard.nextCellId,
        rngState: resolvedBoard.rngState,
        spawned: resolvedBoard.spawned,
      }
  const uniquePathCellCount = countUniquePathCells(state.board, positions)
  const clearedPositions = uniqueClearedPositionsAlongPath(positions)
  let overtimeSurvivingSelectionCount = 0
  for (let index = 0; index < positions.length; index += 1) {
    if (state.selectionAddedDuringOvertime[index] === true) {
      overtimeSurvivingSelectionCount += 1
    }
  }
  const duringMeterBurstWindow =
    isTimerEndGoldRushActive(state) || isGoldRushOvertimeState(state)
  const extraLetterBonusTimeMs = getExtraLetterBonusTimeMs(word, state.playgroundSettings)
  const acceptedWordSpecialTiles = getAcceptedWordSpecialTileOutcome(state, positions)
  const comboSettings = state.playgroundSettings.combo
  const isRepeatWord = state.scoredWords.includes(word)
  const meterIncrease = suppressMeterIncrease
    ? 0
    : getBoggleMeterAcceptedWordIncrease(word, state.playgroundSettings.meter, {
        isRepeat: isRepeatWord,
      })
  const meterAfterAcceptedWord = applyBoggleMeterDelta(
    state.meter,
    meterIncrease,
    state.playgroundSettings.meter,
  )
  const meterGain = Math.max(0, meterAfterAcceptedWord.value - state.meter.value)
  const overmeterGain = Math.max(
    0,
    meterAfterAcceptedWord.overmeterValue - state.meter.overmeterValue,
  )
  // Playing a word does not change combo on its own; only a full clear bumps
  // it by +1 (the word was accepted, then the board emptied, which is a clean
  // sweep). wordIncrement is kept as an optional additive per-word bump and
  // defaults to 0.
  const nextCombo = !comboSettings.enabled
    ? state.combo
    : state.combo + (boardClearedAfterWord ? 1 : 0) + comboSettings.wordIncrement
  const effectiveBlueTimeBonusMs =
    state.intervalDurationMs === null ? 0 : acceptedWordSpecialTiles.blueTimeBonusMs
  const appliedBlueTimeBonusMs =
    blueTileTimeBonusMode === 'apply-now' ? effectiveBlueTimeBonusMs : 0
  const carriedBlueTimeBonusMs =
    blueTileTimeBonusMode === 'carry-to-next-interval' ? effectiveBlueTimeBonusMs : 0
  const runStatLabels: string[] = []
  const timeBonusLabels: string[] = []

  if (acceptedWordSpecialTiles.goldInPath > 0) {
    // Gold is accumulated per submitted path position that carried the tag at
    // resolution time, not just once per accepted word.
    runStatLabels.push(
      `${acceptedWordSpecialTiles.goldInPath} gold`,
    )
  }

  if (extraLetterBonusTimeMs > 0) {
    timeBonusLabels.push(`+${extraLetterBonusTimeMs / 1000}s bonus time`)
  }

  if (effectiveBlueTimeBonusMs > 0) {
    timeBonusLabels.push(
      blueTileTimeBonusMode === 'carry-to-next-interval'
        ? `+${effectiveBlueTimeBonusMs / 1000}s next interval`
        : blueTileTimeBonusMode === 'apply-now'
          ? `+${effectiveBlueTimeBonusMs / 1000}s blue time`
          : '',
    )
  }
  const filteredTimeBonusLabels = timeBonusLabels.filter(Boolean)

  const runStatLabel =
    runStatLabels.length === 0 ? '' : ` including ${runStatLabels.join(' and ')}`
  const timeBonusLabel =
    filteredTimeBonusLabels.length === 0
      ? ''
      : ` and ${filteredTimeBonusLabels.join(' and ')}`

  const resolutionStagger = applyTileSpawnStagger(
    state.playgroundSettings,
    resolvedBoard.moved,
    repopulated.spawned,
  )

  const nextState: BoggleGameState = {
    ...state,
    board: repopulated.board,
    combo: nextCombo,
    feedback: {
      kind: 'success',
      message: `${word} cleared ${uniquePathCellCount} tiles${runStatLabel}${timeBonusLabel}. ${describeBoardAftermath(state)}`,
      untilMs: state.timeMs + BOGGLE_INVALID_FEEDBACK_MS,
    },
    lastAction: {
      atMs: state.timeMs,
      goldTilesUsed: acceptedWordSpecialTiles.goldInPath,
      kind: 'accepted',
      message: `${word} cleared.`,
      tilesCleared: uniquePathCellCount,
      word,
    },
    lastResolution: {
      cleared: resolvedBoard.cleared,
      durationMs: resolutionStagger.durationMs,
      movementDurationMs: resolutionStagger.movementDurationMs,
      moved: resolvedBoard.moved,
      spawned: resolutionStagger.spawned,
      startedAtMs: state.timeMs,
      word,
    },
    letterBags: repopulated.letterBags,
    meter: meterAfterAcceptedWord,
    nextCellId: repopulated.nextCellId,
    recentWords: uniqueRecentWords(state.recentWords, word),
    rngState: repopulated.rngState,
    runLedger: [
      ...state.runLedger,
      {
        atMs: state.timeMs,
        acceptedDuringOvertime: state.isInOvertime,
        boardCols: state.cols,
        boardRows: state.rows,
        clearedPositions,
        duringMeterBurstWindow,
        formedWithoutBackspace: !state.hadBackspaceWhileFormingWord,
        burstActiveAtAcceptance:
          state.meter.burstActive || meterAfterAcceptedWord.burstActive,
        index: state.runLedger.length,
        kind: 'accepted-word',
        meterCapacity: state.playgroundSettings.meter.capacity,
        meterGain,
        meterBurstActiveAfter: meterAfterAcceptedWord.burstActive,
        meterOvermeterAfter: meterAfterAcceptedWord.overmeterValue,
        meterValueAfter: meterAfterAcceptedWord.value,
        overmeterGain,
        overtimeSurvivingSelectionCount,
        path: positions.map(position => ({ ...position })),
        pathSteps: acceptedWordSpecialTiles.pathSteps,
        effectiveSpecialTileCountsUsed:
          acceptedWordSpecialTiles.effectiveSpecialTileCountsUsed,
        physicalSpecialTileCountsUsed:
          acceptedWordSpecialTiles.physicalSpecialTileCountsUsed,
        tileLetters: positions.map(
          position => state.board[position.row]?.[position.col]?.letter ?? '',
        ),
        uncommonLetterCount: countBoggleMeterUncommonLetters(
          word,
          state.playgroundSettings.meter.increase.acceptedWord,
        ),
        uniqueTilesCleared: uniquePathCellCount,
        word,
      },
      ...(!state.meter.burstActive && meterAfterAcceptedWord.burstActive
        ? [
            {
              atMs: state.timeMs,
              index: state.runLedger.length + 1,
              kind: 'phase' as const,
              phase: 'meter-burst' as const,
              transition: 'start' as const,
            },
          ]
        : []),
    ],
    scoredWords: [...state.scoredWords, word],
    goldTilesUsed: state.goldTilesUsed + acceptedWordSpecialTiles.goldInPath,
    selection: [],
    selectionMode: null,
    selectionAddedDuringOvertime: [],
    hadBackspaceWhileFormingWord: false,
    isInOvertime: preserveOvertime ? state.isInOvertime : false,
    overtimeBackspaceCount: preserveOvertime ? state.overtimeBackspaceCount : 0,
    overtimePauseUntilMs: preserveOvertime ? state.overtimePauseUntilMs : null,
    overtimeRemaining: preserveOvertime ? state.overtimeRemaining : null,
    overtimeVelocity: preserveOvertime ? state.overtimeVelocity : 0,
    pendingIntervalTimeBonusMs: state.pendingIntervalTimeBonusMs + carriedBlueTimeBonusMs,
    specialTileSpawnCadenceCounts: resolvedBoard.specialTileSpawnCadenceCounts,
    spawnedTileCount: resolvedBoard.spawnedTileCount,
    skillSelection: null,
    intervalDeadlineMs:
      state.intervalDeadlineMs === null
        ? null
        : state.intervalDeadlineMs + extraLetterBonusTimeMs + appliedBlueTimeBonusMs,
    tilesCleared: state.tilesCleared + uniquePathCellCount,
    totalLettersCleared:
      state.totalLettersCleared +
      (state.playgroundSettings.redrawBoardAfterWord
        ? resolvedBoard.cleared.length
        : uniquePathCellCount),
    wordsCleared: state.wordsCleared + 1,
  }

  return skipNoPlayableWordsCheck ? nextState : endRunIfNoPlayableWordsRemain(nextState)
}

function createLetterCell(letter: string, nextCellId: number, tags?: readonly string[]) {
  return {
    cell: {
      id: nextCellId,
      letter,
      tags,
    } satisfies BoggleCell,
    nextCellId: nextCellId + 1,
  }
}

function clearMatchingSkillSelection(state: BoggleGameState, mode: BoggleSkillMode) {
  if (state.skillSelection?.mode !== mode) {
    return state
  }

  return {
    ...state,
    feedback: null,
    skillSelection: null,
  }
}

function createSkillSuccessState(
  state: BoggleGameState,
  message: string,
  {
    activeSkill = state.activeSkill,
    board = state.board,
    lastResolution = null,
    copiesRemaining = state.copiesRemaining,
    letterBags = state.letterBags,
    nextCellId = state.nextCellId,
    pendingIntervalTimeBonusMs = state.pendingIntervalTimeBonusMs,
    resetsRemaining = state.resetsRemaining,
    rngState = state.rngState,
    shufflesRemaining = state.shufflesRemaining,
    specialTileSpawnCadenceCounts = state.specialTileSpawnCadenceCounts,
    spawnedTileCount = state.spawnedTileCount,
    skillSelection = null,
    swapsRemaining = state.swapsRemaining,
    zapsRemaining = state.zapsRemaining,
  }: {
    activeSkill?: BoggleGameState['activeSkill']
    board?: BoggleBoard
    lastResolution?: BoggleGameState['lastResolution']
    skillSelection?: BoggleSkillSelection | null
    copiesRemaining?: number
    letterBags?: BoggleLetterBags
    nextCellId?: number
    pendingIntervalTimeBonusMs?: number
    resetsRemaining?: number
    rngState?: number
    selectionMode?: BoggleSelectionMode | null
    shufflesRemaining?: number
    specialTileSpawnCadenceCounts?: BoggleSpecialTileCadenceCounts
    spawnedTileCount?: number
    swapsRemaining?: number
    zapsRemaining?: number
  } = {},
): BoggleGameState {
  return {
    ...state,
    activeSkill,
    board,
    feedback: {
      kind: 'success',
      message,
      untilMs: state.timeMs + BOGGLE_INVALID_FEEDBACK_MS,
    },
    lastAction: {
      atMs: state.timeMs,
      kind: 'skill',
      message,
      word: null,
    },
    lastResolution,
    copiesRemaining,
    letterBags,
    nextCellId,
    pendingIntervalTimeBonusMs,
    resetsRemaining,
    rngState,
    selection: [],
    selectionMode: null,
    shufflesRemaining,
    specialTileSpawnCadenceCounts,
    spawnedTileCount,
    skillSelection,
    swapsRemaining,
    zapsRemaining,
  }
}

function rotateBoardWithGravity(
  board: BoggleBoard,
  dimensions: GridDimensions,
  direction: BoggleRotationDirection,
) {
  const originLookup = new Map<number, BogglePosition>()
  const rotatedLookup = new Map<number, BogglePosition>()

  for (let row = 0; row < dimensions.rows; row += 1) {
    for (let col = 0; col < dimensions.cols; col += 1) {
      const cell = board[row]?.[col]
      if (!cell) {
        continue
      }

      originLookup.set(cell.id, { col, row })
    }
  }

  const rotatedDimensions = {
    cols: dimensions.rows,
    rows: dimensions.cols,
  } satisfies GridDimensions
  const rotated = rotateLetterBoard(board, dimensions, direction)
  for (let row = 0; row < rotatedDimensions.rows; row += 1) {
    for (let col = 0; col < rotatedDimensions.cols; col += 1) {
      const cell = rotated[row]?.[col]
      if (!cell) {
        continue
      }

      rotatedLookup.set(cell.id, { col, row })
    }
  }
  const collapsed = collapseBoardDownward(rotated, rotatedDimensions)
  const moved: BoggleAnimatedCell[] = []

  for (let row = 0; row < rotatedDimensions.rows; row += 1) {
    for (let col = 0; col < rotatedDimensions.cols; col += 1) {
      const cell = collapsed.board[row]?.[col]
      if (!cell) {
        continue
      }

      const origin = originLookup.get(cell.id)
      const rotatedPosition = rotatedLookup.get(cell.id)
      if (!origin) {
        continue
      }

      const hasIntermediateMove =
        rotatedPosition !== undefined &&
        (rotatedPosition.row !== origin.row || rotatedPosition.col !== origin.col)
      const hasFinalMove = origin.row !== row || origin.col !== col
      if (!hasIntermediateMove && !hasFinalMove) {
        continue
      }

      moved.push({
        col,
        fromCol: origin.col,
        fromRow: origin.row,
        id: cell.id,
        letter: cell.letter,
        tags: cell.tags,
        toCol: col,
        toRow: row,
        viaCol: rotatedPosition?.col,
        viaRow: rotatedPosition?.row,
      })
    }
  }

  return {
    board: collapsed.board,
    dimensions: rotatedDimensions,
    moved,
  }
}

function isResolvedChallengePracticeRun(
  resolvedChallenge: BoggleGameState['resolvedChallenge'],
): boolean {
  return (
    resolvedChallenge != null &&
    'practiceSeed' in resolvedChallenge &&
    typeof (resolvedChallenge as { practiceSeed: unknown }).practiceSeed === 'number'
  )
}

export function createBoggleGameState(
  options: CreateBoggleGameOptions = {},
): BoggleGameState {
  const devAcceptAnyDictionaryWord = options.devAcceptAnyDictionaryWord ?? false
  const seed = normalizeSeed(options.seed ?? BOGGLE_DEFAULT_SEED)
  const playgroundSettings = createDefaultSettings(options.playgroundSettings)
  const dimensions = getBoggleGridDimensions(playgroundSettings.gridSize)
  const resolvedChallenge = options.resolvedChallenge ?? null
  const playable = createPlayableBoard(playgroundSettings, seed, 1, resolvedChallenge, {
    applyOpeningSpecialTiles: true,
  })
  const intervalDurationMs = getIntervalDurationMs(playgroundSettings)
  const skipIntervalPrepCountdown =
    intervalDurationMs !== null && isResolvedChallengePracticeRun(resolvedChallenge)
  const intervalPlayBeginsAtMs =
    intervalDurationMs === null
      ? null
      : skipIntervalPrepCountdown
        ? null
        : BOGGLE_INTERVAL_PREP_MS
  const readyMessage =
    playgroundSettings.intervalCount === null
      ? `Seed ${seed} ready.`
      : `Seed ${seed} ready. ${createTimerReadyMessage(
          playgroundSettings.intervalCount,
          playgroundSettings.intervalTimeSeconds,
        )}`

  return {
    activeSkill: null,
    allowDiagonals: playgroundSettings.allowDiagonals,
    board: playable.board,
    cols: dimensions.cols,
    combo: playgroundSettings.combo.enabled ? playgroundSettings.combo.startingValue : 0,
    copiesRemaining: getBoggleSkillUses(playgroundSettings.skills.copy),
    devAcceptAnyDictionaryWord,
    endReason: null,
    feedback: null,
    letterAdjacencyMode: playgroundSettings.letterAdjacencyMode,
    letterBags: playable.letterBags,
    lastAction: {
      atMs: 0,
      kind: 'reset',
      message: readyMessage,
      word: null,
    },
    lastResolution: null,
    lettersRepopulate: playgroundSettings.lettersRepopulate,
    goldTilesUsed: 0,
    intervalDeadlineMs:
      intervalDurationMs === null
        ? null
        : skipIntervalPrepCountdown
          ? intervalDurationMs
          : null,
    intervalDurationMs,
    intervalPlayBeginsAtMs,
    intervalsRemaining: playgroundSettings.intervalCount,
    isInOvertime: false,
    meter: createBoggleMeterState(playgroundSettings.meter),
    minWordLength: playgroundSettings.minWordLength,
    nextCellId: playable.nextCellId,
    overtimeBackspaceCount: 0,
    overtimePauseUntilMs: null,
    overtimeRemaining: null,
    overtimeVelocity: 0,
    pendingIntervalTimeBonusMs: 0,
    phase: 'playing',
    playgroundSettings,
    recentWords: [],
    resolvedChallenge,
    runLedger: [],
    scoredWords: [],
    resetsRemaining: getBoggleSkillUses(playgroundSettings.skills.reset),
    repeatTileMinGap: getResolvedRepeatTileMinGap(
      playgroundSettings.letterAdjacencyMode,
      dimensions,
      resolvedChallenge,
    ),
    rngState: playable.rngState,
    rotationEnabled: playgroundSettings.rotationEnabled,
    rows: dimensions.rows,
    seed,
    selection: [],
    selectionAddedDuringOvertime: [],
    hadBackspaceWhileFormingWord: false,
    selectionMode: null,
    shufflesRemaining: getBoggleSkillUses(playgroundSettings.skills.shuffle),
    specialTileSpawnCadenceCounts: createEmptyBoggleSpecialTileCadenceCounts(),
    spawnedTileCount: 0,
    skillSelection: null,
    swapsRemaining: getBoggleSkillUses(playgroundSettings.skills.swap),
    timeMs: 0,
    tilesCleared: 0,
    totalLettersCleared: 0,
    wordsCleared: 0,
    zapsRemaining: getBoggleSkillUses(playgroundSettings.skills.zap),
  }
}

export function createDebugBoggleState(
  rows: string[],
  options: CreateBoggleGameOptions & {
    rngState?: number
    timeMs?: number
  } = {},
) {
  const seed = normalizeSeed(options.seed ?? BOGGLE_DEFAULT_SEED)
  const normalizedRows = rows.map(row => row.replace(/\s+/g, '').toUpperCase())
  const rowCount = normalizedRows.length
  const colCount = normalizedRows[0]?.length ?? 0
  let nextCellId = 1
  const board = normalizedRows.map(row =>
    row.split('').map(letter => ({
      id: nextCellId++,
      letter,
      tags: undefined,
    })),
  )
  const playgroundSettings = normalizeBogglePlaygroundSettings(
    {
      ...options.playgroundSettings,
      gridSize: toBoggleGridSize({ cols: colCount, rows: rowCount }),
    },
    createDefaultSettings(options.playgroundSettings),
  )
  const boardWithSpecialTiles = applyOpeningSpecialTiles(
    board,
    { cols: colCount, rows: rowCount },
    playgroundSettings,
    options.rngState ?? seed,
  )

  return {
    ...createBoggleGameState(options),
    allowDiagonals: playgroundSettings.allowDiagonals,
    board: boardWithSpecialTiles.board,
    combo: playgroundSettings.combo.enabled ? playgroundSettings.combo.startingValue : 0,
    nextCellId,
    playgroundSettings,
    copiesRemaining: getBoggleSkillUses(playgroundSettings.skills.copy),
    cols: colCount,
    letterAdjacencyMode: playgroundSettings.letterAdjacencyMode,
    lettersRepopulate: playgroundSettings.lettersRepopulate,
    meter: createBoggleMeterState(playgroundSettings.meter),
    minWordLength: playgroundSettings.minWordLength,
    pendingIntervalTimeBonusMs: 0,
    resetsRemaining: getBoggleSkillUses(playgroundSettings.skills.reset),
    rngState: boardWithSpecialTiles.rngState,
    rotationEnabled: playgroundSettings.rotationEnabled,
    repeatTileMinGap: getResolvedRepeatTileMinGap(
      playgroundSettings.letterAdjacencyMode,
      { cols: colCount, rows: rowCount },
      options.resolvedChallenge ?? null,
    ),
    rows: rowCount,
    seed,
    shufflesRemaining: getBoggleSkillUses(playgroundSettings.skills.shuffle),
    spawnedTileCount: 0,
    intervalDeadlineMs:
      getIntervalDurationMs(playgroundSettings) === null
        ? null
        : getIntervalDurationMs(playgroundSettings),
    intervalDurationMs: getIntervalDurationMs(playgroundSettings),
    intervalPlayBeginsAtMs: null,
    intervalsRemaining: playgroundSettings.intervalCount,
    isInOvertime: false,
    overtimeBackspaceCount: 0,
    overtimePauseUntilMs: null,
    overtimeRemaining: null,
    overtimeVelocity: 0,
    specialTileSpawnCadenceCounts: createEmptyBoggleSpecialTileCadenceCounts(),
    timeMs: options.timeMs ?? 0,
    tilesCleared: 0,
    swapsRemaining: getBoggleSkillUses(playgroundSettings.skills.swap),
    zapsRemaining: getBoggleSkillUses(playgroundSettings.skills.zap),
  }
}

export function resetBoggleGame(
  state: BoggleGameState,
  options: BoggleResetOptions = {},
) {
  return createBoggleGameState({
    devAcceptAnyDictionaryWord:
      options.devAcceptAnyDictionaryWord ?? state.devAcceptAnyDictionaryWord,
    playgroundSettings: normalizeBogglePlaygroundSettings(
      options.playgroundSettings ?? {},
      state.playgroundSettings,
    ),
    resolvedChallenge: options.resolvedChallenge ?? state.resolvedChallenge,
    seed: options.seed ?? state.seed,
  })
}

export function applyBogglePlaygroundSettings(
  state: BoggleGameState,
  updates: BogglePlaygroundSettingsInput,
) {
  const nextSettings = normalizeBogglePlaygroundSettings(
    updates,
    state.playgroundSettings,
  )
  if (areBogglePlaygroundSettingsEqual(nextSettings, state.playgroundSettings)) {
    return state
  }

  // Playground changes always start a fresh run on the current seed so the board and rules stay in sync.
  return resetBoggleGame(state, {
    playgroundSettings: nextSettings,
    resolvedChallenge: state.resolvedChallenge,
    seed: state.seed,
  })
}

export function shouldAdvanceBoggleClock(state: BoggleGameState) {
  return (
    (state.phase === 'playing' &&
      (state.intervalDeadlineMs !== null ||
        state.isInOvertime ||
        (state.playgroundSettings.meter.enabled &&
          state.meter.value > 0 &&
          (state.playgroundSettings.meter.naturalDecayPerSecond > 0 ||
            state.meter.burstActive)) ||
        hasMeterTileSpawnWork(state) ||
        (state.intervalPlayBeginsAtMs !== null &&
          state.timeMs < state.intervalPlayBeginsAtMs))) ||
    (state.feedback !== null && state.feedback.untilMs !== null) ||
    state.lastResolution !== null
  )
}

export function rotateBoard(
  state: BoggleGameState,
  direction: BoggleRotationDirection,
): BoggleGameState {
  if (!isPlaying(state)) {
    return state
  }

  if (!state.rotationEnabled) {
    return createInvalidState(state, 'Rotation is disabled in the playground.', '')
  }

  const nextBoard = rotateBoardWithGravity(
    state.board,
    getStateDimensions(state),
    direction,
  )
  const directionLabel = direction === 'left' ? 'left' : 'right'
  const message = `Board rotated ${directionLabel}. Gravity pulled letters downward.`

  return {
    ...state,
    board: nextBoard.board,
    cols: nextBoard.dimensions.cols,
    feedback: {
      kind: 'success',
      message,
      untilMs: state.timeMs + BOGGLE_INVALID_FEEDBACK_MS,
    },
    lastAction: {
      atMs: state.timeMs,
      kind: 'rotate',
      message,
      word: null,
    },
    lastResolution: {
      cleared: [],
      durationMs: BOGGLE_ROTATE_ANIMATION_MS + BOGGLE_ROTATE_GRAVITY_ANIMATION_MS,
      gravityStartsAtMs: BOGGLE_ROTATE_ANIMATION_MS,
      moved: nextBoard.moved,
      spawned: [],
      startedAtMs: state.timeMs,
      word: `ROTATE ${directionLabel.toUpperCase()}`,
    },
    rows: nextBoard.dimensions.rows,
    selection: [],
    selectionAddedDuringOvertime: [],
    hadBackspaceWhileFormingWord: false,
    selectionMode: null,
    skillSelection: null,
  }
}

export function beginSelection(
  state: BoggleGameState,
  position: BogglePosition,
  selectionMode: BoggleSelectionMode = 'drag',
) {
  if (!isPlaying(state)) {
    return state
  }

  if (isIntervalPrepBlockingPlay(state)) {
    return state
  }

  const nextSelection = gridTryBeginSelection(
    state.board,
    getStateDimensions(state),
    position,
  )
  if (nextSelection === null) {
    return state
  }

  const nextState = applyOvertimeLetterPress({
    ...clearSelectionState(state),
    feedback: null,
    selection: nextSelection,
    selectionAddedDuringOvertime: [state.isInOvertime],
    hadBackspaceWhileFormingWord: false,
    selectionMode,
  })
  const cell = state.board[position.row]?.[position.col]

  return cell === null || cell === undefined
    ? nextState
    : appendSemanticAction(nextState, {
        actionKind: 'selected-letter',
        letter: cell.letter,
        position: { ...position },
      })
}

export function isPlayableSelectionPosition(
  state: BoggleGameState,
  position: BogglePosition,
) {
  if (!isPlaying(state) || state.activeSkill !== null) {
    return false
  }

  if (isIntervalPrepBlockingPlay(state)) {
    return false
  }

  if (state.selection.length === 0) {
    return (
      gridTryBeginSelection(state.board, getStateDimensions(state), position) !== null
    )
  }

  const { change, selection: nextSelection } = gridUpdateSelection(
    state.board,
    getStateDimensions(state),
    state.allowDiagonals,
    state.selection,
    position,
    state.repeatTileMinGap,
    requiresStepAdjacency(state),
    forbidsStepAdjacency(state),
  )

  if (change.type !== 'append') {
    return false
  }

  const maxWordLength = getMaxWordLength(state.resolvedChallenge)
  if (maxWordLength !== null && nextSelection.length > maxWordLength) {
    return false
  }

  return true
}

export function updateSelection(
  state: BoggleGameState,
  position: BogglePosition,
  selectionMode = state.selectionMode ?? 'drag',
) {
  if (!isPlaying(state)) {
    return {
      change: { type: 'ignored' } satisfies BoggleSelectionChange,
      state,
    }
  }

  if (isIntervalPrepBlockingPlay(state)) {
    return {
      change: { type: 'ignored' } satisfies BoggleSelectionChange,
      state,
    }
  }

  const { change, selection } = gridUpdateSelection(
    state.board,
    getStateDimensions(state),
    state.allowDiagonals,
    state.selection,
    position,
    state.repeatTileMinGap,
    requiresStepAdjacency(state),
    forbidsStepAdjacency(state),
  )

  if (change.type === 'ignored' && selection === state.selection) {
    return {
      change: { type: 'ignored' } satisfies BoggleSelectionChange,
      state,
    }
  }

  const selectionAddedDuringOvertime =
    change.type === 'append'
      ? [...state.selectionAddedDuringOvertime, state.isInOvertime]
      : change.type === 'backtrack'
        ? state.selectionAddedDuringOvertime.slice(0, selection.length)
        : state.selectionAddedDuringOvertime

  const nextState = {
    ...state,
    selection,
    selectionAddedDuringOvertime,
    selectionMode,
  }
  const resolvedState =
    change.type === 'append' ? applyOvertimeLetterPress(nextState) : nextState
  const appendedCell =
    change.type === 'append' ? state.board[position.row]?.[position.col] : null

  return {
    change,
    state:
      change.type === 'append' && appendedCell
        ? appendSemanticAction(resolvedState, {
            actionKind: 'selected-letter',
            letter: appendedCell.letter,
            position: { ...position },
          })
        : resolvedState,
  }
}

export function tapSelection(state: BoggleGameState, position: BogglePosition) {
  if (!isPlaying(state)) {
    return state
  }

  if (state.selection.length === 0) {
    return beginSelection(state, position, 'tap')
  }

  return updateSelection(state, position, 'tap').state
}

export function getSubmitSelectionValidationMessage(
  state: BoggleGameState,
): string | null {
  if (!isPlaying(state) || state.selection.length === 0) {
    return null
  }

  if (isIntervalPrepBlockingPlay(state)) {
    return 'Not playable yet'
  }

  const word = standardLexicon.getBoardWord(state.board, state.selection)
  const wordRuleValidationMessage = getWordRuleValidationMessage(state, word)
  if (wordRuleValidationMessage !== null) {
    return wordRuleValidationMessage
  }

  if (!isSubmittedWordInDictionary(state, word)) {
    return 'Not in dictionary'
  }

  if (isAlreadyScoredWord(state, word)) {
    return 'Already played'
  }

  return null
}

export function canSubmitSelection(state: BoggleGameState) {
  return getSubmitSelectionValidationMessage(state) === null
}

function advanceClockState(state: BoggleGameState, elapsedMs: number): BoggleGameState {
  const nextTime = state.timeMs + elapsedMs
  const feedbackExpired =
    state.feedback !== null &&
    state.feedback.untilMs !== null &&
    nextTime >= state.feedback.untilMs
  const resolutionExpired =
    state.lastResolution !== null &&
    nextTime >= state.lastResolution.startedAtMs + state.lastResolution.durationMs
  const meter = isPlaying(state)
    ? decayBoggleMeter(state.meter, elapsedMs, state.playgroundSettings.meter)
    : state.meter
  const meterTileSpawns = isPlaying(state)
    ? applyMeterTileSpawns(state, elapsedMs, meter)
    : { board: state.board, meter, rngState: state.rngState }

  return {
    ...state,
    board: meterTileSpawns.board,
    feedback: feedbackExpired ? null : state.feedback,
    lastResolution: resolutionExpired ? null : state.lastResolution,
    meter: meterTileSpawns.meter,
    rngState: meterTileSpawns.rngState,
    timeMs: nextTime,
  }
}

function resolveOvertimeExpiry(state: BoggleGameState) {
  if (!state.isInOvertime) {
    return state
  }

  if (isGoldRushOvertimeState(state)) {
    if (!canSubmitSelection(state)) {
      return createTimeoutEndState(state)
    }

    const submitted = applyWordClear(
      state,
      state.selection,
      standardLexicon.getBoardWord(state.board, state.selection),
      {
        blueTileTimeBonusMode: 'apply-now',
        skipNoPlayableWordsCheck: true,
        suppressMeterIncrease: true,
      },
    )

    return resolveAcceptedTimedExpiry(submitted)
  }

  if (canSubmitSelection(state)) {
    const blueTileTimeBonusMode =
      (state.intervalsRemaining ?? 0) > 1
        ? getBlueTileTimeBonusModeForAcceptedWord(state, 'timeout')
        : 'apply-now'
    const submitted = applyWordClear(
      state,
      state.selection,
      standardLexicon.getBoardWord(state.board, state.selection),
      {
        blueTileTimeBonusMode,
        skipNoPlayableWordsCheck: true,
      },
    )
    return resolveAcceptedOvertimeState(submitted)
  }

  return resolveExpiredTimedInterval(state, 'timeout')
}

function getOvertimeDrainTimeMs(overtimeRemaining: number, overtimeVelocity: number) {
  const accelerationPerMs = BOGGLE_OVERTIME_ACCELERATION_PER_SECOND / 1_000_000
  const velocityPerMs = overtimeVelocity / 1000
  const discriminant =
    velocityPerMs * velocityPerMs + 2 * accelerationPerMs * overtimeRemaining

  return Math.ceil((-velocityPerMs + Math.sqrt(discriminant)) / accelerationPerMs)
}

function advanceOvertimeState(state: BoggleGameState, elapsedMs: number) {
  let remainingMs = elapsedMs
  let consumedMs = 0
  let nextState = state

  while (remainingMs > 0 && nextState.isInOvertime) {
    const pauseRemainingMs =
      nextState.overtimePauseUntilMs === null
        ? 0
        : Math.max(0, nextState.overtimePauseUntilMs - nextState.timeMs)
    if (pauseRemainingMs > 0) {
      const pausedStep = Math.min(remainingMs, pauseRemainingMs)
      nextState = advanceClockState(nextState, pausedStep)
      remainingMs -= pausedStep
      consumedMs += pausedStep
      continue
    }

    if (nextState.overtimeRemaining === null) {
      return {
        elapsedMs: consumedMs,
        state: nextState,
      }
    }

    const drainTimeMs = getOvertimeDrainTimeMs(
      nextState.overtimeRemaining,
      nextState.overtimeVelocity,
    )

    if (drainTimeMs <= remainingMs) {
      const elapsedSeconds = drainTimeMs / 1000
      nextState = resolveOvertimeExpiry({
        ...advanceClockState(nextState, drainTimeMs),
        overtimeRemaining: 0,
        overtimeVelocity:
          nextState.overtimeVelocity +
          BOGGLE_OVERTIME_ACCELERATION_PER_SECOND * elapsedSeconds,
      })
      remainingMs -= drainTimeMs
      consumedMs += drainTimeMs
      continue
    }

    const elapsedSeconds = remainingMs / 1000
    const drained =
      nextState.overtimeVelocity * elapsedSeconds +
      0.5 * BOGGLE_OVERTIME_ACCELERATION_PER_SECOND * elapsedSeconds * elapsedSeconds

    nextState = {
      ...advanceClockState(nextState, remainingMs),
      overtimeRemaining: clamp(
        nextState.overtimeRemaining - drained,
        0,
        BOGGLE_OVERTIME_START_VALUE,
      ),
      overtimeVelocity:
        nextState.overtimeVelocity +
        BOGGLE_OVERTIME_ACCELERATION_PER_SECOND * elapsedSeconds,
    }
    consumedMs += remainingMs
    remainingMs = 0
  }

  return {
    elapsedMs: consumedMs,
    state: nextState,
  }
}

export function finishSelection(state: BoggleGameState) {
  if (!isPlaying(state)) {
    return state
  }

  if (isIntervalPrepBlockingPlay(state)) {
    return state
  }

  if (state.selection.length === 0) {
    if (state.selectionMode === null) {
      return state
    }

    return {
      ...state,
      selectionMode: null,
    }
  }

  const word = standardLexicon.getBoardWord(state.board, state.selection)
  const withSubmitAction = appendSemanticAction(state, {
    actionKind: 'submit',
    word,
  })
  const wordRuleValidationMessage = getWordRuleValidationMessage(state, word)
  if (wordRuleValidationMessage !== null) {
    return applyMeterDeltaForEvent(
      createInvalidState(withSubmitAction, wordRuleValidationMessage, word),
      -withSubmitAction.playgroundSettings.meter.decrease.invalidWord,
    )
  }

  if (!isSubmittedWordInDictionary(state, word)) {
    return applyMeterDeltaForEvent(
      createInvalidState(
        withSubmitAction,
        `${word} is not in the prototype dictionary.`,
        word,
      ),
      -withSubmitAction.playgroundSettings.meter.decrease.invalidWord,
    )
  }

  if (isAlreadyScoredWord(state, word)) {
    return applyMeterDeltaForEvent(
      createInvalidState(withSubmitAction, 'Already played', word),
      -withSubmitAction.playgroundSettings.meter.decrease.invalidWord,
    )
  }

  const goldRushOvertime = isGoldRushOvertimeState(withSubmitAction)
  const clearedState = applyWordClear(withSubmitAction, withSubmitAction.selection, word, {
    blueTileTimeBonusMode: getBlueTileTimeBonusModeForAcceptedWord(
      withSubmitAction,
      'submit',
    ),
    skipNoPlayableWordsCheck:
      withSubmitAction.isInOvertime ||
      (withSubmitAction.playgroundSettings.endIntervalOnSubmit &&
        withSubmitAction.intervalDurationMs !== null),
    suppressMeterIncrease: goldRushOvertime,
  })

  return withSubmitAction.isInOvertime
    ? resolveAcceptedOvertimeState(clearedState)
    : withSubmitAction.playgroundSettings.endIntervalOnSubmit &&
        withSubmitAction.intervalDurationMs !== null
      ? advanceIntervalAfterSubmission(clearedState)
      : clearedState
}

function withClearSelectionCorrection(state: BoggleGameState): BoggleGameState {
  return {
    ...clearSelectionState(state),
    runLedger: [
      ...state.runLedger,
      {
        kind: 'correction' as const,
        correctionKind: 'clear' as const,
        index: state.runLedger.length,
        atMs: state.timeMs,
      },
    ],
  }
}

export function clearSkillSelection(state: BoggleGameState) {
  return clearActiveSkill(state)
}

export function clearSelection(state: BoggleGameState) {
  if (!isPlaying(state)) {
    return state
  }

  if (state.selection.length === 0) {
    return clearActiveSkill(state)
  }

  if (state.intervalDurationMs === null) {
    return applyMeterDeltaForEvent(
      withClearSelectionCorrection(state),
      -state.playgroundSettings.meter.decrease.clearSelection,
    )
  }

  if (isGoldRushOvertimeState(state)) {
    return withClearSelectionCorrection(state)
  }

  return state.isInOvertime
    ? resolveExpiredTimedInterval(withClearSelectionCorrection(state), 'clear')
    : applyMeterDeltaForEvent(
        withClearSelectionCorrection(state),
        -state.playgroundSettings.meter.decrease.clearSelection,
      )
}

export function zapTile(state: BoggleGameState, position: BogglePosition) {
  if (!isPlaying(state)) {
    return state
  }

  if (!isSkillEnabled(state, 'zap')) {
    return createInvalidState(state, 'Zap is disabled in the playground.', '')
  }

  const cell = state.board[position.row]?.[position.col]
  if (!cell) {
    return createInvalidState(state, 'That space is already empty.', '')
  }

  if (state.zapsRemaining <= 0) {
    return createInvalidState(state, 'No zaps remaining.', '')
  }

  const settledZapBoard = settleBoardAfterRemoval(state, [position])
  const zapDimensions = getStateDimensions(state)
  const afterZapResetWhenEmpty = applyBoardResetWhenSpecialTilesEmpty(
    state.playgroundSettings,
    zapDimensions,
    settledZapBoard.board,
    settledZapBoard.spawned,
    settledZapBoard.rngState,
  )
  const zapStagger = applyTileSpawnStagger(
    state.playgroundSettings,
    settledZapBoard.moved,
    afterZapResetWhenEmpty.spawned,
  )
  return endRunIfNoPlayableWordsRemain(
    createSkillSuccessState(
      state,
      `Zapped ${cell.letter}. ${describeBoardAftermath(state)}`,
      {
        activeSkill: null,
        board: afterZapResetWhenEmpty.board,
        lastResolution: {
          cleared: settledZapBoard.cleared,
          durationMs: zapStagger.durationMs,
          movementDurationMs: zapStagger.movementDurationMs,
          moved: settledZapBoard.moved,
          spawned: zapStagger.spawned,
          startedAtMs: state.timeMs,
          word: `ZAP ${cell.letter}`,
        },
        nextCellId: settledZapBoard.nextCellId,
        rngState: afterZapResetWhenEmpty.rngState,
        zapsRemaining: state.zapsRemaining - 1,
      },
    ),
  )
}

function resetBoardSkill(state: BoggleGameState) {
  if (!isPlaying(state)) {
    return state
  }

  if (!isSkillEnabled(state, 'reset')) {
    return createInvalidState(state, 'Reset is disabled in the playground.', '', {
      activeSkill: state.activeSkill,
      skillSelection: state.skillSelection,
    })
  }

  if (state.resetsRemaining <= 0) {
    return createInvalidState(state, 'No resets remaining.', '', {
      activeSkill: state.activeSkill,
      skillSelection: state.skillSelection,
    })
  }

  const dimensions = getStateDimensions(state)
  const lettersOnBoardBeforeReset = getFilledPositions(state.board, dimensions).length
  const rerolled = createResetRerolledBoard(state)
  const cleared = getFilledBoardCells(state.board, dimensions)
  const spawned = getSpawnedBoardCells(rerolled.board, dimensions)
  const resetStagger = applyTileSpawnStagger(state.playgroundSettings, [], spawned)

  const successState = createSkillSuccessState(
    state,
    'Board rerolled with the current special tile mix.',
    {
      activeSkill: null,
      board: rerolled.board,
      lastResolution: {
        cleared,
        durationMs: resetStagger.durationMs,
        movementDurationMs: resetStagger.movementDurationMs,
        moved: [],
        spawned: resetStagger.spawned,
        startedAtMs: state.timeMs,
        word: 'REROLL BOARD',
      },
      letterBags: rerolled.letterBags,
      nextCellId: rerolled.nextCellId,
      resetsRemaining: state.resetsRemaining - 1,
      rngState: rerolled.rngState,
      skillSelection: null,
    },
  )

  // Combo rule for the manual reset skill, based on letters present when the
  // player pressed Reset:
  //   0 letters -> +1 (treated the same as a word fully clearing the board)
  //   1 letter  -> unchanged (low-stakes reset, no penalty)
  //   2+ letters -> 0 (big rebuild, combo is lost)
  return {
    ...successState,
    combo: !state.playgroundSettings.combo.enabled
      ? state.combo
      : lettersOnBoardBeforeReset === 0
        ? state.combo + 1
        : lettersOnBoardBeforeReset === 1
          ? state.combo
          : 0,
  }
}

function shuffleBoardSkill(state: BoggleGameState) {
  if (!isPlaying(state)) {
    return state
  }

  if (!isSkillEnabled(state, 'shuffle')) {
    return createInvalidState(state, 'Shuffle is disabled in the playground.', '', {
      activeSkill: state.activeSkill,
      skillSelection: state.skillSelection,
    })
  }

  if (state.shufflesRemaining <= 0) {
    return createInvalidState(state, 'No shuffles remaining.', '', {
      activeSkill: state.activeSkill,
      skillSelection: state.skillSelection,
    })
  }

  const filledCells = getFilledBoardCells(state.board, getStateDimensions(state))
  if (filledCells.length < 2) {
    return createInvalidState(state, 'Need at least two filled tiles to shuffle.', '', {
      activeSkill: state.activeSkill,
      skillSelection: state.skillSelection,
    })
  }

  const shuffled = shuffleFilledBoardCells(
    state.board,
    getStateDimensions(state),
    state.rngState,
  )

  return createSkillSuccessState(state, 'Board shuffled with the current letters.', {
    activeSkill: null,
    board: shuffled.board,
    lastResolution: {
      cleared: [],
      durationMs: BOGGLE_RESOLVE_ANIMATION_MS,
      moved: shuffled.moved,
      spawned: [],
      startedAtMs: state.timeMs,
      word: 'SHUFFLE BOARD',
    },
    rngState: shuffled.rngState,
    shufflesRemaining: state.shufflesRemaining - 1,
    skillSelection: null,
  })
}

function primeSkill(
  state: BoggleGameState,
  mode: BoggleSkillSelection['mode'],
  position: BogglePosition,
  message: string,
) {
  return createSkillSuccessState(state, message, {
    activeSkill: mode,
    lastResolution: null,
    skillSelection: { mode, position },
  })
}

function getSkillUsesRemaining(state: BoggleGameState, mode: BoggleSkillMode) {
  switch (mode) {
    case 'reset':
      return state.resetsRemaining
    case 'shuffle':
      return state.shufflesRemaining
    case 'zap':
      return state.zapsRemaining
    case 'swap':
      return state.swapsRemaining
    case 'copy':
      return state.copiesRemaining
  }
}

function getSkillReadyMessage(mode: BoggleSkillMode) {
  switch (mode) {
    case 'reset':
      return 'Reset ready. Tap again to clear and refill the whole board.'
    case 'shuffle':
      return 'Shuffle ready. Tap again to scramble the current letters.'
    case 'zap':
      return 'Zap ready. Tap one tile to remove it.'
    case 'swap':
      return 'Swap ready. Tap one tile, then tap a second tile.'
    case 'copy':
      return 'Copy ready. Tap one tile, then tap a destination tile.'
  }
}

function clearActiveSkill(state: BoggleGameState) {
  if (
    state.activeSkill === null &&
    state.skillSelection === null &&
    state.selection.length === 0 &&
    state.selectionMode === null
  ) {
    return state
  }

  return {
    ...state,
    activeSkill: null,
    feedback: null,
    selection: [],
    selectionMode: null,
    skillSelection: null,
  }
}

export function toggleSkillMode(state: BoggleGameState, mode: BoggleSkillMode) {
  if (!isPlaying(state)) {
    return state
  }

  if (state.activeSkill === mode) {
    return clearActiveSkill(state)
  }

  if (!isSkillEnabled(state, mode)) {
    return createInvalidState(
      state,
      `${mode[0]!.toUpperCase()}${mode.slice(1)} is disabled in the playground.`,
      '',
      {
        activeSkill: state.activeSkill,
        selection: state.selection,
        skillSelection: state.skillSelection,
      },
    )
  }

  if (getSkillUsesRemaining(state, mode) <= 0) {
    return createInvalidState(state, `No ${mode}s remaining.`, '', {
      activeSkill: state.activeSkill,
      selection: state.selection,
      skillSelection: state.skillSelection,
    })
  }

  if (mode === 'reset') {
    return resetBoardSkill(state)
  }

  if (mode === 'shuffle') {
    return shuffleBoardSkill(state)
  }

  return createSkillSuccessState(state, getSkillReadyMessage(mode), {
    activeSkill: mode,
    lastResolution: null,
    skillSelection: null,
  })
}

export function swapTile(state: BoggleGameState, position: BogglePosition) {
  if (!isPlaying(state)) {
    return state
  }

  if (!isSkillEnabled(state, 'swap')) {
    return createInvalidState(state, 'Swap is disabled in the playground.', '', {
      activeSkill: state.activeSkill,
      skillSelection: state.skillSelection,
    })
  }

  const cell = state.board[position.row]?.[position.col]
  if (!cell) {
    return createInvalidState(state, 'Only filled tiles can be swapped.', '', {
      activeSkill: state.activeSkill,
      skillSelection: state.skillSelection,
    })
  }

  if (state.skillSelection === null || state.skillSelection.mode !== 'swap') {
    if (state.swapsRemaining <= 0) {
      return createInvalidState(state, 'No swaps remaining.', '', {
        activeSkill: state.activeSkill,
        skillSelection: state.skillSelection,
      })
    }

    return primeSkill(
      state,
      'swap',
      position,
      `Swap primed on ${cell.letter}. Tap a second tile.`,
    )
  }

  if (
    state.skillSelection.position.row === position.row &&
    state.skillSelection.position.col === position.col
  ) {
    return {
      ...state,
      feedback: null,
      selection: [],
      skillSelection: null,
    }
  }

  const first =
    state.board[state.skillSelection.position.row]?.[state.skillSelection.position.col]
  if (!first) {
    return createInvalidState(
      state,
      'The selected swap tile is no longer available.',
      '',
      {
        activeSkill: state.activeSkill,
        skillSelection: null,
      },
    )
  }

  const nextBoard = state.board.map(row => [...row])
  nextBoard[state.skillSelection.position.row]![state.skillSelection.position.col] = cell
  nextBoard[position.row]![position.col] = first

  return createSkillSuccessState(state, `Swapped ${first.letter} with ${cell.letter}.`, {
    activeSkill: null,
    board: nextBoard,
    swapsRemaining: state.swapsRemaining - 1,
    skillSelection: null,
  })
}

export function copyTile(state: BoggleGameState, position: BogglePosition) {
  if (!isPlaying(state)) {
    return state
  }

  if (!isSkillEnabled(state, 'copy')) {
    return createInvalidState(state, 'Copy is disabled in the playground.', '', {
      activeSkill: state.activeSkill,
      skillSelection: state.skillSelection,
    })
  }

  const cell = state.board[position.row]?.[position.col]
  if (!cell) {
    return createInvalidState(state, 'Only filled tiles can be copied.', '', {
      activeSkill: state.activeSkill,
      skillSelection: state.skillSelection,
    })
  }

  if (state.skillSelection === null || state.skillSelection.mode !== 'copy') {
    if (state.copiesRemaining <= 0) {
      return createInvalidState(state, 'No copies remaining.', '', {
        activeSkill: state.activeSkill,
        skillSelection: state.skillSelection,
      })
    }

    return primeSkill(
      state,
      'copy',
      position,
      `Copy primed on ${cell.letter}. Tap a target tile.`,
    )
  }

  if (
    state.skillSelection.position.row === position.row &&
    state.skillSelection.position.col === position.col
  ) {
    return {
      ...state,
      feedback: null,
      selection: [],
      skillSelection: null,
    }
  }

  const source =
    state.board[state.skillSelection.position.row]?.[state.skillSelection.position.col]
  if (!source) {
    return createInvalidState(
      state,
      'The selected copy tile is no longer available.',
      '',
      {
        activeSkill: state.activeSkill,
        skillSelection: null,
      },
    )
  }

  const nextBoard = state.board.map(row => [...row])
  const duplicated = createLetterCell(source.letter, state.nextCellId, source.tags)
  nextBoard[position.row]![position.col] = duplicated.cell

  return {
    ...createSkillSuccessState(state, `Copied ${source.letter} onto ${cell.letter}.`, {
      activeSkill: null,
      board: nextBoard,
      copiesRemaining: state.copiesRemaining - 1,
      skillSelection: null,
    }),
    nextCellId: duplicated.nextCellId,
  }
}

export function altClickSwap(state: BoggleGameState, position: BogglePosition) {
  return swapTile(state, position)
}

export function ctrlClickCopy(state: BoggleGameState, position: BogglePosition) {
  return copyTile(state, position)
}

export function useActiveSkillOnTile(state: BoggleGameState, position: BogglePosition) {
  if (!isPlaying(state)) {
    return state
  }

  switch (state.activeSkill) {
    case 'reset':
      return resetBoardSkill(state)
    case 'shuffle':
      return shuffleBoardSkill(state)
    case 'zap':
      return zapTile(state, position)
    case 'swap':
      return swapTile(state, position)
    case 'copy':
      return copyTile(state, position)
    case null:
      return state
  }
}

export function submitPath(state: BoggleGameState, positions: BogglePosition[]) {
  if (!isPlaying(state) || positions.length === 0) {
    return state
  }

  let nextState = beginSelection(state, positions[0]!)
  for (const position of positions.slice(1)) {
    nextState = updateSelection(nextState, position).state
  }
  return finishSelection(nextState)
}

export function advanceBoggleTime(state: BoggleGameState, ms: number) {
  if (!isPlaying(state)) {
    if (state.lastResolution !== null) {
      return armIntervalAfterPrep(advanceClockState(state, ms))
    }

    return state
  }

  let remainingMs = Math.max(0, Math.floor(ms))
  let nextState = armIntervalAfterPrep(state)

  while (remainingMs > 0 && isPlaying(nextState)) {
    if (nextState.isInOvertime) {
      const overtimeStep = advanceOvertimeState(nextState, remainingMs)
      nextState = overtimeStep.state
      remainingMs -= overtimeStep.elapsedMs
      continue
    }

    const meterBurstTimeRemainingMs = getTimerEndGoldRushMeterTimeRemainingMs(nextState)
    if (meterBurstTimeRemainingMs !== null) {
      if (meterBurstTimeRemainingMs <= 0) {
        nextState = enterOvertime(nextState)
        continue
      }

      if (remainingMs < meterBurstTimeRemainingMs) {
        return armIntervalAfterPrep(advanceClockState(nextState, remainingMs))
      }

      nextState = armIntervalAfterPrep(
        advanceClockState(nextState, meterBurstTimeRemainingMs),
      )
      remainingMs -= meterBurstTimeRemainingMs
      nextState = enterOvertime(nextState)
      continue
    }

    const intervalTimeRemainingMs = getIntervalTimeRemainingMs(nextState)
    if (intervalTimeRemainingMs === null) {
      const prepEndsAt = nextState.intervalPlayBeginsAtMs
      if (prepEndsAt !== null && nextState.timeMs < prepEndsAt) {
        const prepRemaining = prepEndsAt - nextState.timeMs
        const step = Math.min(remainingMs, prepRemaining)
        nextState = armIntervalAfterPrep(advanceClockState(nextState, step))
        remainingMs -= step
        continue
      }

      return armIntervalAfterPrep(advanceClockState(nextState, remainingMs))
    }

    if (remainingMs < intervalTimeRemainingMs) {
      return armIntervalAfterPrep(advanceClockState(nextState, remainingMs))
    }

    nextState = armIntervalAfterPrep(
      advanceClockState(nextState, intervalTimeRemainingMs),
    )
    remainingMs -= intervalTimeRemainingMs

    nextState = shouldStartTimerEndGoldRush(nextState)
      ? enterTimerEndGoldRush(nextState)
      : nextState.selection.length > 0
        ? enterOvertime(nextState)
        : resolveExpiredTimedInterval(nextState, 'timeout')
  }

  return armIntervalAfterPrep(nextState)
}
